import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { communityTypeToIssuingAuthorityType } from '@tsr/shared-types';
import type {
  ActiveFireFeature,
  AircraftFeature,
  CameraFeature,
  CapAlertFeature,
  HighRiskHotspotFeature,
  RawHotspotFeature,
  CameraSmokeFeature,
  UtilityOutageFeature,
  RoadClosureFeature,
  ArcgisFeature,
  OperatorEvacOrderFeature,
  CommsDegradationZoneFeature,
  ResponseAssetFeature,
  SensorFeature,
  SearchPlace,
  SearchEntity,
} from '@tsr/shared-types';
import { LayerToggle } from './LayerToggle';
import { OcFireWatchLine } from './OcFireWatchLine';
import { LeftRail } from './LeftRail';
import { CoordinationPanel } from './CoordinationPanel';
import { NewsSocialFeed } from './NewsSocialFeed';
import { BriefingView } from './BriefingView';
import { AnomalyQueuePanel } from './AnomalyQueuePanel';
import { ShiftLogPanel } from './ShiftLogPanel';
import {
  MapView,
  type SectorController,
  type EvacuationOrderController,
  type CommunityController,
  type FloodInundationController,
} from './MapView';
import { FloodInundationControl } from './FloodInundationControl';
import { type FlythroughController } from './flythrough';
import { type FlythroughTarget } from './flythroughPath';
import { type AarReplayController } from './aarReplay';
import { AarReplayOverlay } from './AarReplayOverlay';
import type { AarReplayResponse } from '@tsr/shared-types';
import { SidePanel, type Selection, type ColocatedIncidentRef } from './SidePanel';
import { SearchBox } from './SearchBox';
import { CameraOverlay } from './CameraOverlay';
import { FwiLegend } from './FwiLegend';
import {
  anyWildfireLayerOn,
  EMPTY_ASSET_COUNTS,
  fetchServerLayerPrefs,
  fireResponseTypeFilter,
  fireStageFilter,
  loadLayerPrefs,
  queueServerLayerSync,
  saveLayerPrefs,
  type AssetCounts,
  type LayerPrefs,
} from './prefs';
import {
  fetchOntarioCorpsPartners,
  fetchCommunityDetail,
  fetchCommunityEvacuationZone,
  patchEvacuationOrderGeometry,
  patchCommunity,
  fetchIncidentForSource,
  fetchIncidentReplay,
  createIncident,
  fetchSitrep,
  createSector,
  updateSectorGeometry,
  fetchAssistanceRequest,
  type BboxQuery,
  type OntarioCorpsPartner,
  type VesselFeature,
  type DroneCoverageFeature,
  type DroneMissionView,
  type SmokeAnomalyFeature,
  type IceStormAnomalyFeature,
  type FloodAnomalyFeature,
  type MediaSignalAnomalyFeature,
  type HydrometricGaugeFeature,
  type CommunityFeature,
} from './api';
import { headcountBadge } from './layers/CommunitiesLayer';
import { arcgisVisibilityFromPrefs } from './layers/arcgisCategories';
import { wmsVisibilityFromPrefs } from './layers/wmsOverlays';
import { CommunityEntityForm } from './CommunityEntityForm';
import {
  EvacuationOrderForm,
  type EvacuationOrderFormPreseed,
  type EvacuationZoneProvenance,
} from './EvacuationOrderForm';
import type {
  WellnessCheckStatus,
  StagingArea,
  Sector,
  Community,
  AgencyTaskView,
} from '@tsr/shared-types';
import { usePopoutRegistry } from './popout';
import { loadPins, replaceAllPins, usePinnedFeeds } from './pinnedFeeds';
import { usePanelPresence } from './panelBridge';
import { useSelectionBridge, type BridgeSelection } from './selectionBridge';
import { ToastHost, showToast } from './lib/toast';
import {
  computeAreaCommandNotice,
  type AreaCommandIdentity,
  type AreaCommandNoticeState,
} from './areaCommandFirstNotice';
import { type SitrepIncidentSelection } from './SitrepControls';
import { SitrepDetailPanel } from './SitrepDetailPanel';
import { CommandBar } from './CommandBar';
import { AssistanceRequestDetail } from './AssistanceRequestDetail';
import type {
  SitrepReport,
  AssistanceRequest,
  SavedSeatProfile,
  Incident,
  IncidentSourceRecordType,
} from '@tsr/shared-types';
import { logAction, getSessionId } from './lib/operatorActions';
import {
  type FlyController,
  type CameraView,
  CANADA_VIEW_BOUNDS,
  flyNationalView,
} from './cameraFly';
import { loadHomeView } from './homeView';
import {
  fetchSavedViews,
  persistHomeView,
  persistRegion,
  persistSeatProfile,
  deleteSavedView,
  type SavedRegion,
} from './savedViews';
import {
  applyProfileLayerDeltas,
  snapshotProfileView,
  resolveBootLayerPrefs,
  enterMunicipalLens,
  exitMunicipalLens,
  type SeatProfileTarget,
} from './seatProfiles';
import { useAuth } from './auth/useAuth';
import { isImsRole } from '@tsr/shared-types';
import { useMediaQuery } from './lib/useMediaQuery';
import { WIDE_DISPLAY_QUERY } from './lib/wideDisplay';
import { useFeedHealth } from './lib/useFeedHealth';
import { useApiHealth } from './lib/useApiHealth';
import { roadClosureCoverageState } from './roadClosureCoverage';
import { ApiHealthBanner } from './ApiHealthBanner';

/** True when the URL carries a deep-link the cold-start framing defers to — an
 *  `#at=` map point or an entity link (#cam/#fire/#hotspot/#evacuation/#asset).
 *  When set, the #155 server-home reconciliation does NOT yank the camera to home
 *  on load, matching MapView's first-paint precedence (#at > entity > home > Canada). */
function hasDeepLinkInHash(): boolean {
  if (typeof window === 'undefined') return false;
  return /[#&](at|cam|fire|hotspot|evacuation|asset)=/.test(window.location.hash);
}

export function App(): JSX.Element {
  const [prefs, setPrefs] = useState<LayerPrefs>(() => loadLayerPrefs());
  // #458 (role-view council §7 step 4) — the operator's IMS role for the role→seat
  // FIRST-RUN boot scaffold, narrowed to a COP role (the AuthGate guarantees only
  // COP / demo principals reach <App/>). null when unauthenticated (the AUTH-off
  // demo identity) or a non-COP value → the scaffold seeds nothing (DEFAULT view).
  const { user, status: authStatus } = useAuth();
  const bootRole = user && isImsRole(user.role) ? user.role : null;
  const bootSeedAppliedRef = useRef(false);
  // #458 — the PEOC "view as <community>" TRANSIENT lens (council §3). The community
  // being glanced (drives the banner); a snapshot of the operator's real prefs +
  // map extent to restore on exit; and a flag that makes the lens NON-PERSISTENT
  // (suppresses the localStorage write + bypasses any server sync while it is up).
  const [viewAsCommunity, setViewAsCommunity] = useState<CommunityFeature | null>(null);
  const transientViewActiveRef = useRef(false);
  const transientSnapshotRef = useRef<LayerPrefs | null>(null);
  const transientMapViewRef = useRef<CameraView | null>(null);
  const [selected, setSelected] = useState<Selection>(null);
  // The camera shown in the click-to-fly overlay (Phase V). Distinct from
  // `selected`: the side panel tracks `selected`, the overlay tracks this.
  const [overlayCamera, setOverlayCamera] = useState<CameraFeature | null>(null);
  const [cameraCount, setCameraCount] = useState(0);
  const [sensorCount, setSensorCount] = useState(0);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [loadingSensors, setLoadingSensors] = useState(false);
  const [assetCounts, setAssetCounts] = useState<AssetCounts>(EMPTY_ASSET_COUNTS);
  const [aircraftCount, setAircraftCount] = useState(0);
  const [fireCount, setFireCount] = useState(0);
  // #337 — in-view road-closure count (null = not yet known). Feeds the honest
  // data-coverage cue so a covered province's confirmed zero ("no closures reported")
  // stays distinct from an out-of-coverage view's "no feed here".
  const [roadClosureCount, setRoadClosureCount] = useState<number | null>(null);
  // R5 Slice 3 (#507) — live open-Area-Command count. Feeds the layer-picker row
  // label ("Area Command (N)") even while the layer is OFF (PEOC first-notice cue).
  const [areaCommandCount, setAreaCommandCount] = useState(0);
  // #509 — first-notice cue. The grow-only set of every open-Area-Command id seen so far
  // (null = not yet seeded, i.e. boot). The 45s AC poll seeds this on first load, so
  // pre-existing Area Commands never toast; only a never-before-seen Area Command fires the
  // cue (grow-only ⇒ a flicker that drops then restores an AC never re-announces it).
  const areaCommandNoticeRef = useRef<AreaCommandNoticeState>(null);
  // #424 — the ONE urgent-reds count: in-view URGENT reds (RL≥3) emitted by the
  // AnomalyQueuePanel from its own countQueue. Drives BOTH the rail "Alerts" badge
  // AND the Layers-panel "Urgent Alerts" count, so the two never disagree and both
  // span every red source. Replaces the old per-surface map-layer mirrors
  // (highRiskCount + smokeAnomalyCount) that counted fire hotspots only and so
  // under-reported CAP/flood/ice reds (e.g. 2 RL4 cards in view but badge=1).
  // #325 — `null` = UNKNOWN (API unreachable), distinct from 0 = confirmed none. The
  // AnomalyQueuePanel emits null while !reachable so the rail badge renders "—" (via
  // LeftRail) and the Layers "Urgent Alerts" count blanks (via LayerToggle), never a
  // false "0".
  const [urgentAlertCount, setUrgentAlertCount] = useState<number | null>(0);
  const [ontarioCorpsPartners, setOntarioCorpsPartners] = useState<OntarioCorpsPartner[]>([]);
  // SITREP engine (SITREP.1): the live map bbox feeds the "Current map view"
  // scope; activeSitrep is the report shown in the detail modal.
  const [currentBbox, setCurrentBbox] = useState<BboxQuery | null>(null);
  const [activeSitrep, setActiveSitrep] = useState<SitrepReport | null>(null);
  // #185 — the assist request opened via the Alert → ASSIST cross-link from a
  // SidePanel Urgent-Alert body. App owns this modal so the cross-link is
  // independent of the Coordination → Assist worklist (AssistQueuePanel, #378).
  const [connectedAssist, setConnectedAssist] = useState<AssistanceRequest | null>(null);
  // SITL surface — open a SITREP from an incident's report chain in the existing
  // 720px document modal (reuses the same activeSitrep slot as the SitrepControls
  // "Recent" reopen path; view_sitrep is audited by SitrepDetailPanel on mount).
  const handleOpenSitrep = useCallback((id: string) => {
    void fetchSitrep(id)
      .then(setActiveSitrep)
      .catch(() => showToast('Could not open SITREP'));
  }, []);
  // #509 — Area Command first-notice cue. Every AC poll reports the open count + identities;
  // update the picker-row count, then let the pure decision helper (which owns boot-seed
  // suppression + the flicker-safe seen-ever gate) tell us whether to toast and with what copy.
  const handleAreaCommandCountChange = useCallback(
    (count: number, areaCommands: AreaCommandIdentity[]): void => {
      setAreaCommandCount(count);
      const { nextState, toast } = computeAreaCommandNotice(
        areaCommandNoticeRef.current,
        areaCommands,
      );
      areaCommandNoticeRef.current = nextState;
      if (toast) showToast(toast, { variant: 'info' });
    },
    [],
  );
  // Ultrawide enhancement (#131, re-tuned standard-first by #192): only on a
  // genuinely wide display (width > 2560 AND aspect ≥ 2:1 — see wideDisplay.ts)
  // does the open SITREP document dock as an in-flow ~720px reading column
  // beside the detail panel; at canonical widths it stays a centred modal.
  const dockSitrep = useMediaQuery(WIDE_DISPLAY_QUERY);
  // #254 — honest feed-health: polls per-worker heartbeats and derives which
  // layers' backing daemons are dark, for the layer-panel "down since X" chip.
  const feedHealth = useFeedHealth();
  // #325 — honest-degradation: poll /api/health so a TOTAL /api outage stops reading as
  // a clean quiet board (a false all-clear). Drives the full-width banner + the queue's
  // "unavailable" empty-state + the rail badge's "—". Client-side reachability ONLY —
  // never touches cameras/ingestion/MapView.
  const apiHealth = useApiHealth();
  // #337 — honest road-closure data-coverage for the current view. The feed reaches
  // ON/AB/BC only, so the LayerToggle can tell "no feed here" apart from a covered
  // province that genuinely has zero closures (unknown ≠ zero). Display-only.
  const roadClosureCoverage = useMemo(
    () => roadClosureCoverageState(currentBbox, roadClosureCount),
    [currentBbox, roadClosureCount],
  );
  // L.2.2 — Anomaly Queue + new layer counts. (#424 — the smoke-anomaly MAP count
  // no longer feeds the rail badge; that badge now comes from the queue's urgent
  // count, so the standalone smoke count state was removed.)
  const [, setIceAnomalyCount] = useState(0);
  const [, setFloodAnomalyCount] = useState(0);
  // Sticky last-selected incident point (fire/hotspot/smoke) — drives the Drone
  // Coverage reachability highlight + the detail-panel ETA. Persists across a
  // subsequent drone selection (single selection model, §3B.6).
  const [incidentPoint, setIncidentPoint] = useState<{ lat: number; lon: number } | null>(null);
  // Bumped to force the Anomaly Queue to refetch after a smoke confirm/dismiss.
  const [queueRefreshNonce, setQueueRefreshNonce] = useState(0);
  // #387/#378/#374 — aggregate count of OPEN coordination items (tracked incidents +
  // assist requests + drone requests), badged on the LeftRail "Coordination" tab.
  // CoordinationPanel keeps all three sub-panels mounted (they poll regardless of the
  // active tab) and sums their open counts up, so the operator sees pending work
  // without opening the tab.
  const [coordinationCount, setCoordinationCount] = useState(0);
  // #381 — the Shift Log relocated off the rail to the CommandBar Settings ▾ menu;
  // it opens here as a left-edge drawer overlaid on the map region.
  const [shiftLogOpen, setShiftLogOpen] = useState(false);
  // W.2 — client-side community status/badge cache (the W.1 list endpoint omits
  // wellness + headcount, so the map markers colour from lazily-fetched detail).
  const [communityStatuses, setCommunityStatuses] = useState<
    Record<string, { status: WellnessCheckStatus | null; badge: string | null }>
  >({});
  // Ids already fetched/in-flight, so onCommunitiesInView fetches each once.
  const communityFetched = useRef<Set<string>>(new Set());

  // Stable Ontario Corps partner roster for the subtoggles — fetched once the
  // first time the layer is enabled (independent of the map bbox).
  useEffect(() => {
    if (!prefs.ontarioCorps || ontarioCorpsPartners.length > 0) return;
    let cancelled = false;
    void fetchOntarioCorpsPartners()
      .then((names) => {
        if (!cancelled) setOntarioCorpsPartners(names);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [prefs.ontarioCorps, ontarioCorpsPartners.length]);

  const hiddenPartnerSet = useMemo(() => new Set(prefs.hiddenPartners), [prefs.hiddenPartners]);
  const hiddenAircraftRoleSet = useMemo(
    () => new Set(prefs.hiddenAircraftRoles),
    [prefs.hiddenAircraftRoles],
  );

  // Per-category visibility for the generalized public-ArcGIS renderer (#213) —
  // evacuation + fire bans + drought + boil-water + SoLE + cyclones. Memoized so its
  // identity is stable unless a toggle actually changes (MapView's apply-visibility
  // effect depends on it).
  const arcgisCategoryVisibility = useMemo(() => arcgisVisibilityFromPrefs(prefs), [prefs]);

  // #212 — per-key visibility for the WMS raster overlays. Memoized so its
  // identity is stable unless a toggle changes (MapView's apply-visibility effect
  // depends on it, mirroring arcgisCategoryVisibility).
  const wmsOverlayVisibility = useMemo(() => wmsVisibilityFromPrefs(prefs), [prefs]);

  // The currently-selected incident (fire / high-risk hotspot) enables the
  // "Selected incident" SITREP scope; other selections leave it null.
  const sitrepIncident = useMemo<SitrepIncidentSelection | null>(() => {
    if (!selected) return null;
    if (selected.kind === 'fire')
      return { id: selected.feature.properties.id, type: 'active_fire', label: 'Selected fire' };
    if (selected.kind === 'highRiskHotspot')
      return {
        id: selected.feature.properties.id,
        type: 'high_risk_hotspot',
        label: 'Selected hotspot',
      };
    return null;
  }, [selected]);

  // Task #64 — resolve the REAL incident id for the current fire/hotspot selection
  // (sectors FK to incidents.id). null when nothing actionable is selected or the
  // source hasn't been promoted yet (Draw Sector promotes on demand).
  useEffect(() => {
    if (!sitrepIncident) {
      setSelectedIncidentId(null);
      return;
    }
    const ctl = new AbortController();
    void fetchIncidentForSource(
      sitrepIncident.type === 'active_fire' ? 'active_wildland_fire' : 'high_risk_hotspot',
      sitrepIncident.id,
      ctl.signal,
    )
      .then((inc) => setSelectedIncidentId(inc?.id ?? null))
      .catch(() => setSelectedIncidentId(null));
    return () => ctl.abort();
  }, [sitrepIncident]);

  const handleBboxChange = useCallback((b: BboxQuery) => {
    setCurrentBbox(b);
    // #337 — the view changed, so the last road-closure count is stale for this bbox.
    // Reset to UNKNOWN until the road-closures layer re-fetches for the new view; this
    // keeps a covered view from reading a stale "no closures reported — feed is live"
    // (the honest-degradation race a round-3 adversarial pass surfaced).
    setRoadClosureCount(null);
  }, []);

  // MapView fills this once the Cesium viewer exists; App drives fly-to / -back.
  const flyControllerRef = useRef<FlyController | null>(null);
  // Task #64 — MapView fills this with the sector draw tool + layer controls.
  const sectorControllerRef = useRef<SectorController | null>(null);
  const evacuationOrderControllerRef = useRef<EvacuationOrderController | null>(null);
  // W.2 editor — MapView fills this with the place-a-point tool + layer refresh.
  const communityControllerRef = useRef<CommunityController | null>(null);
  // #177 — MapView fills this with the flood-inundation level control + fly-to.
  const floodInundationControllerRef = useRef<FloodInundationController | null>(null);
  // #179 — MapView fills this with the operator 3D-flythrough controller.
  const flythroughControllerRef = useRef<FlythroughController | null>(null);
  // Whether a flythrough is running (drives the panel button label/state).
  const [flythroughActive, setFlythroughActive] = useState(false);
  // #180 — MapView fills this with the AAR time-replay controller; non-null
  // data means the replay overlay is open (live urgent layers suppressed).
  const aarReplayControllerRef = useRef<AarReplayController | null>(null);
  const [aarReplayData, setAarReplayData] = useState<AarReplayResponse | null>(null);
  // What the in-progress place-a-point will become: a new declared community, or a
  // centroid move of an existing one.
  const placeIntentRef = useRef<
    { mode: 'declare' } | { mode: 'move'; community: CommunityFeature } | null
  >(null);
  // Toolbar/hint state while placing a community point.
  const [placingCommunity, setPlacingCommunity] = useState(false);
  // A placed point awaiting the declare form (lon/lat the operator dropped).
  const [pendingDeclare, setPendingDeclare] = useState<{ lon: number; lat: number } | null>(null);
  // The REAL incidents.id resolved for the current fire/hotspot selection (sectors
  // FK to it). null until resolved / promoted. Drives the SectorsLayer + Draw Sector.
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  // Coordination re-arch Phase 2 (Drone Ruling 3) — the incident the operator is
  // working, PERSISTED across the drone-coverage-pin click. selectedIncidentId clears
  // the moment a non-fire/hotspot is selected (a coverage pin's sitrepIncident is
  // null), but the operator clicked that pin precisely to task a drone to the incident
  // they were just working — so DroneCoverageBody needs the id to live past that click.
  // Three rules keep it coherent with the AO point and never stale:
  //   1. a fresh actionable POINT clears it (the new point's incident is unknown until
  //      it resolves) — so it never lags a moved AO;
  //   2. the resolver fills it once a fire/hotspot resolves to a tracked incident;
  //   3. leaving the working context — closing the panel, or selecting anything that
  //      ISN'T the incident (fire/hotspot, re-filled by the resolver) or the
  //      drone-coverage continuation — clears it, so a later coverage-pin task can't
  //      link a drone to a stale/closed incident.
  // Fixes the hardcoded incidentId=null tasking bug without introducing a stale link.
  const [workingIncidentId, setWorkingIncidentId] = useState<string | null>(null);
  useEffect(() => {
    setWorkingIncidentId(null);
  }, [incidentPoint]);
  useEffect(() => {
    if (selectedIncidentId) setWorkingIncidentId(selectedIncidentId);
  }, [selectedIncidentId]);
  useEffect(() => {
    const kind = selected?.kind;
    if (kind !== 'fire' && kind !== 'highRiskHotspot' && kind !== 'droneCoverage') {
      setWorkingIncidentId(null);
    }
  }, [selected]);
  // What an in-progress draw will become on completion (create new vs edit geom).
  const sectorDrawTargetRef = useRef<
    { mode: 'create'; incidentId: string } | { mode: 'edit'; sectorId: string } | null
  >(null);
  // A freshly-drawn ring awaiting a name (the create naming form).
  const [pendingSector, setPendingSector] = useState<{
    ring: Array<[number, number]>;
    incidentId: string;
  } | null>(null);
  const [pendingName, setPendingName] = useState('');
  const [pendingLead, setPendingLead] = useState('');
  const [drawingSector, setDrawingSector] = useState(false);
  // #445-(3) — operator-declared evac-order draw state. The community context that
  // pre-seeds the issuing authority is held in a ref during the draw (mirrors
  // sectorDrawTargetRef); the drawn ring + mapped preseed drive the declare modal.
  const evacDrawPreseedRef = useRef<{
    communityId: string;
    communityName: string;
    communityType: string;
  } | null>(null);
  // #457 — re-entry guard so a double-click doesn't fire two zone fetches / previews.
  const evacOneClickBusyRef = useRef(false);
  // #459 — when set, the NEXT drawn evac ring CORRECTS this existing order's geometry
  // (PATCH) instead of opening the declare modal. Cleared on completion / cancel.
  const evacGeometryEditRef = useRef<{ orderId: string } | null>(null);
  const [pendingEvacOrder, setPendingEvacOrder] = useState<{
    ring: Array<[number, number]>;
    preseed: EvacuationOrderFormPreseed;
    // #457 — present only for the one-click "evacuate this community" path.
    geometrySource?: EvacuationZoneProvenance;
  } | null>(null);
  const [drawingEvacOrder, setDrawingEvacOrder] = useState(false);
  // The incident whose sectors the map renders: the resolved fire/hotspot
  // incident, OR — when a sector itself is selected — that sector's incident, so
  // viewing a sector never clears its sibling sectors from the map.
  const sectorIncidentId = useMemo<string | null>(() => {
    if (selected?.kind === 'sector') return selected.sector.incident_id;
    return selectedIncidentId;
  }, [selected, selectedIncidentId]);
  // Camera ids with an open pop-out window → their markers pulse on the map.
  const pulsingCameraIds = usePopoutRegistry();
  // #196 — pinned-feeds store (cross-window). The pin *action* lives here on the
  // COP/rail; the pinned feeds render in the operations panel (#197) media region.
  const { toggle: togglePinnedFeed, isPinned: isFeedPinned } = usePinnedFeeds();
  const isCameraPinned = useCallback((id: string) => isFeedPinned('camera', id), [isFeedPinned]);
  const handleTogglePinCamera = useCallback(
    (c: CameraFeature) => {
      // Pins are personal workstation state (localStorage + cross-window sync),
      // not an operational audit event — no operator_actions verb is logged.
      togglePinnedFeed({ kind: 'camera', id: c.id, label: c.properties.name });
    },
    [togglePinnedFeed],
  );
  // #197 — true while an operations panel window is open. Drives the two-queue
  // guard (the Urgent Alert queue renders in the COP rail OR the panel, never both).
  const panelOpen = usePanelPresence();
  // #197 — open the operations panel in a second window (dual-monitor seat).
  const openOperationsPanel = useCallback(() => {
    window.open('/panel', 'tsr-ops-panel', 'width=1280,height=800');
  }, []);

  useEffect(() => {
    // #458 — while the PEOC "view as community" lens is up, `prefs` holds the
    // transient municipal glance; never persist it (else the glance stealth-
    // persists as the boot default). On exit, exitViewAsCommunity flips the ref
    // OFF before restoring the snapshot, so the restored real prefs DO save here.
    if (transientViewActiveRef.current) return;
    saveLayerPrefs(prefs);
  }, [prefs]);

  // L.1.3 server-side layer-preference sync + #458 role→seat FIRST-RUN boot.
  // Runs ONCE, after auth resolves (so `bootRole` is final — the gated path
  // splashes until ready; demo mode resolves to a null user fast). resolveBoot-
  // LayerPrefs collapses the whole decision into ONE race-free setPrefs (front-end
  // architect's blocker): a returning operator's stored prefs win; a fresh
  // operator is seeded from their role's seat profile (unmapped/admin → DEFAULT
  // view, byte-identical); an offline/5xx GET leaves the localStorage cache in
  // place (L.1.5 #1/#2). No separately-applied seed exists for a later async boot
  // to clobber.
  useEffect(() => {
    if (authStatus !== 'ready') return;
    if (bootSeedAppliedRef.current) return;
    // Mark the boot as done only on a REAL (non-aborted) resolve — so a StrictMode
    // dev double-mount (mount → abort → remount) re-fetches instead of silently
    // skipping the merge (the abort cancels the first GET).
    let aborted = false;
    const ctl = new AbortController();
    void fetchServerLayerPrefs(ctl.signal).then((server) => {
      if (aborted) return;
      bootSeedAppliedRef.current = true;
      setPrefs((prev) => {
        // If the PEOC lens is somehow open when boot resolves (effectively
        // impossible — boot resolves near-mount, the lens needs user nav+click),
        // land the boot on the operator's SNAPSHOT (restored on exit), leaving the
        // glance onscreen — never clobber the lens with the boot view.
        if (transientViewActiveRef.current && transientSnapshotRef.current) {
          transientSnapshotRef.current = resolveBootLayerPrefs(
            transientSnapshotRef.current,
            server,
            bootRole,
          );
          return prev;
        }
        return resolveBootLayerPrefs(prev, server, bootRole);
      });
    });
    return () => {
      aborted = true;
      ctl.abort();
    };
  }, [authStatus, bootRole]);

  // Layer-toggle changes flow through here so we can diff against the previous
  // state and queue a debounced server PUT for the keys that actually changed
  // (L.1.3 / L.1.5 #3). localStorage persistence stays in the effect above.
  const handlePrefsChange = useCallback((next: LayerPrefs) => {
    setPrefs((prev) => {
      // #458 — during the transient "view as community" lens, toggles stay
      // interactive (council §5.1 retains capability) but must NOT persist: they
      // are part of the throwaway glance, discarded on exit by the snapshot
      // restore. Skip the server PUT (the localStorage write is already suppressed
      // by the save effect above) so the glance can never leak to the server.
      if (!transientViewActiveRef.current) queueServerLayerSync(next, prev);
      return next;
    });
  }, []);

  // B.1 — Briefing-Up mode switch. The choice persists per-operator via the same
  // operator_layer_preferences store (briefing-mode key) as every other toggle.
  const setBriefingMode = useCallback((on: boolean) => {
    setPrefs((prev) => {
      if (prev.briefingMode === on) return prev;
      const next = { ...prev, briefingMode: on };
      // #458 — don't persist (or audit) a toggle made during the transient lens.
      if (!transientViewActiveRef.current) {
        queueServerLayerSync(next, prev);
        logAction('toggle_layer', { layer: 'briefing-mode', enabled: on });
      }
      return next;
    });
  }, []);

  // #178/#262 — viewshed-on-select: selecting a camera auto-drapes its terrain
  // viewshed (founder ruling). This is the per-operator "hide viewshed" opt-out
  // from the camera panel; persists via the same operator_layer_preferences
  // store (viewshed-on-select key) as every other toggle. Honest default ON.
  const setViewshedOnSelect = useCallback((on: boolean) => {
    setPrefs((prev) => {
      if (prev.viewshedOnSelect === on) return prev;
      const next = { ...prev, viewshedOnSelect: on };
      // #458 — don't persist (or audit) a toggle made during the transient lens.
      if (!transientViewActiveRef.current) {
        queueServerLayerSync(next, prev);
        logAction('toggle_layer', { layer: 'viewshed-on-select', enabled: on });
      }
      return next;
    });
  }, []);

  // Select a camera, dive the Cesium camera to it, then fade the overlay in
  // once the flight completes. Also opens the side panel (unchanged flow). If
  // the viewer isn't ready yet (e.g. a deep-link arriving before first paint),
  // show the overlay without the flight rather than dropping it.
  const focusCamera = useCallback((feature: CameraFeature) => {
    setSelected({ kind: 'camera', feature });
    const [lon, lat] = feature.geometry.coordinates;
    const ctrl = flyControllerRef.current;
    if (ctrl) {
      void ctrl.flyToCamera(lat, lon).then(() => setOverlayCamera(feature));
    } else {
      setOverlayCamera(feature);
    }
  }, []);

  // #194 — "Back to map" / Esc / backdrop dismisses the video overlay only. It
  // deliberately does NOT clear the selection or fly the camera back out: losing
  // your place mid-incident is wrong for an ops tool (control-panel spec §1). The
  // camera stays in the rail and the map stays where it is, until something else
  // is selected. (The prior-pose flyBack the dive captured is now intentionally
  // unused — selecting the camera is a navigation, not a round-trip.)
  const closeOverlay = useCallback(() => {
    setOverlayCamera(null);
  }, []);

  // Deep-link: #cam=<uuid> preselects a camera (shareable links; also drives
  // deterministic screenshots). Fetches the detail feature so the side panel
  // — including the live player — and the fly-to overlay open without a click.
  useEffect(() => {
    const m = /[#&]cam=([0-9a-fA-F-]{36})/.exec(window.location.hash);
    if (!m) return undefined;
    let cancelled = false;
    void fetch(`/api/cameras/${m[1]}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: CameraFeature | null) => {
        if (!cancelled && feat && (feat as { type?: string }).type === 'Feature') {
          focusCamera(feat);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [focusCamera]);

  // Deep-link: #asset=<uuid> preselects a response asset (shareable links +
  // deterministic screenshots). The detail payload is a superset of a list
  // feature, so it slots straight into the side panel; AssetMedia re-fetches
  // the same id for children/aircraft.
  useEffect(() => {
    const m = /[#&]asset=([0-9a-fA-F-]{36})/.exec(window.location.hash);
    if (!m) return undefined;
    let cancelled = false;
    void fetch(`/api/response-assets/${m[1]}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: (ResponseAssetFeature & { geometry: unknown }) | null) => {
        if (!cancelled && feat && (feat as { type?: string }).type === 'Feature' && feat.geometry) {
          setSelected({ kind: 'asset', feature: feat });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Deep-link: #fire=<uuid> preselects an active wildland fire (shareable links
  // + deterministic screenshots). Fetches the detail feature so the side panel
  // opens without a click, and flies the camera to the incident centroid.
  useEffect(() => {
    const m = /[#&]fire=([0-9a-fA-F-]{36})/.exec(window.location.hash);
    if (!m) return undefined;
    let cancelled = false;
    void fetch(`/api/active-fires/${m[1]}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: ActiveFireFeature | null) => {
        if (cancelled || !feat || (feat as { type?: string }).type !== 'Feature') return;
        setSelected({ kind: 'fire', feature: feat });
        const [lon, lat] = feat.properties.centroid;
        // R.3.1 — deep-link opens the same ~20 km incident viewport a click does,
        // and is audited the same way (incident zoom is a navigation either path).
        flyControllerRef.current?.flyToIncident(lat, lon);
        logAction('zoom_to_incident', {
          entity_id: feat.properties.id,
          entity_type: 'active_fire',
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Deep-link: #hotspot=<uuid> preselects an AI-fused high-risk hotspot
  // (shareable links + deterministic E.2.3 verification). Fetches the detail
  // feature so the side panel opens without a click, and flies to the point.
  useEffect(() => {
    const m = /[#&]hotspot=([0-9a-fA-F-]{36})/.exec(window.location.hash);
    if (!m) return undefined;
    let cancelled = false;
    void fetch(`/api/high-risk-hotspots/${m[1]}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: HighRiskHotspotFeature | null) => {
        if (cancelled || !feat || (feat as { type?: string }).type !== 'Feature') return;
        setSelected({ kind: 'highRiskHotspot', feature: feat });
        const [lon, lat] = feat.geometry.coordinates;
        // R.3.1 — deep-link opens the same ~20 km incident viewport a click does,
        // and is audited the same way (incident zoom is a navigation either path).
        flyControllerRef.current?.flyToIncident(lat, lon);
        logAction('zoom_to_incident', {
          entity_id: feat.properties.id,
          entity_type: 'high_risk_hotspot',
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Deep-link: #evacuation=<incidentId> opens the §4.4 Evacuation Logistics panel
  // for an incident directly (shareable link to the air-evac surface + deterministic
  // verification). The panel itself fetches + assembles; we just fly to the centroid.
  useEffect(() => {
    const m = /[#&]evacuation=([0-9a-fA-F-]{36})/.exec(window.location.hash);
    if (!m) return undefined;
    const incidentId = m[1]!;
    let cancelled = false;
    void fetch(`/api/incidents/${incidentId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((inc: { id?: string; lat?: number; lon?: number } | null) => {
        if (cancelled || !inc?.id) return;
        setSelected({ kind: 'evacuation', incidentId });
        if (inc.lat != null && inc.lon != null) {
          flyControllerRef.current?.flyToIncident(inc.lat, inc.lon);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectCamera = useCallback(
    (c: CameraFeature | null) => {
      if (c) {
        focusCamera(c);
      } else {
        // Click on empty map: an explicit deselect — clear the rail + dismiss any
        // open overlay. (Map position is left untouched; #194 keeps your place.)
        setSelected(null);
        setOverlayCamera(null);
      }
    },
    [focusCamera],
  );
  // #195 — a co-located camera cluster (zoom can't separate the cams) drops a
  // selectable list into the rail. We deliberately do NOT fly the camera: zooming
  // wouldn't pull the cams apart, so the list is the resolution. Picking one from
  // the list (CameraListBody → onSelectCamera) then focuses + flies to it.
  const handleSelectCameraCluster = useCallback((cameras: CameraFeature[]) => {
    setSelected({ kind: 'cameraList', cameras });
  }, []);
  // #415 (Phase 6b) — a co-located stack of ACT incidents the operator clicked. Like
  // the camera cluster, zoom CAN separate these (screen collision, not one physical
  // site), but until then the rail lists them so each is individually selectable.
  const handleSelectIncidentCluster = useCallback((incidents: ColocatedIncidentRef[]) => {
    setSelected({ kind: 'incidentList', incidents });
  }, []);
  // Pick one incident from that list — the ref IS a valid Selection, so just open it.
  const handleSelectColocatedIncident = useCallback((ref: ColocatedIncidentRef) => {
    setSelected(ref);
  }, []);
  const handleSelectSensor = useCallback((s: SensorFeature | null) => {
    setSelected(s ? { kind: 'sensor', feature: s } : null);
  }, []);
  const handleSelectAsset = useCallback((a: ResponseAssetFeature | null) => {
    setSelected(a ? { kind: 'asset', feature: a } : null);
  }, []);
  const handleSelectAircraft = useCallback((a: AircraftFeature | null) => {
    setSelected(a ? { kind: 'aircraft', feature: a } : null);
  }, []);
  const handleSelectFire = useCallback((f: ActiveFireFeature | null) => {
    setSelected(f ? { kind: 'fire', feature: f } : null);
    if (f) {
      const [lon, lat] = f.properties.centroid;
      setIncidentPoint({ lat, lon });
    }
  }, []);
  const handleSelectHighRiskHotspot = useCallback((h: HighRiskHotspotFeature | null) => {
    setSelected(h ? { kind: 'highRiskHotspot', feature: h } : null);
    if (h) {
      const [lon, lat] = h.geometry.coordinates;
      setIncidentPoint({ lat, lon });
    }
  }, []);
  const handleSelectCapAlert = useCallback((c: CapAlertFeature | null) => {
    setSelected(c ? { kind: 'capAlert', feature: c } : null);
  }, []);
  // E.W.0.5 — raw hotspot selection (ambient pre-fusion signal; no incident fly,
  // does NOT set incidentPoint — it isn't an actionable incident).
  const handleSelectRawHotspot = useCallback((h: RawHotspotFeature | null) => {
    setSelected(h ? { kind: 'rawHotspot', feature: h } : null);
  }, []);
  // E.W.5 — camera smoke selection (ambient pre-fusion camera-CV signal; no
  // incident fly, does NOT set incidentPoint — it isn't an actionable incident).
  const handleSelectCameraSmoke = useCallback((c: CameraSmokeFeature | null) => {
    setSelected(c ? { kind: 'cameraSmoke', feature: c } : null);
  }, []);
  // Power Outages — outage selection (ambient operational signal; no incident fly,
  // does NOT set incidentPoint — it isn't an actionable incident).
  const handleSelectUtilityOutage = useCallback((o: UtilityOutageFeature | null) => {
    setSelected(o ? { kind: 'utilityOutage', feature: o } : null);
  }, []);
  // Road Closures — closure selection (ambient operational signal; no incident fly,
  // does NOT set incidentPoint — it isn't an actionable incident).
  const handleSelectRoadClosure = useCallback((c: RoadClosureFeature | null) => {
    setSelected(c ? { kind: 'roadClosure', feature: c } : null);
  }, []);
  // Public-ArcGIS category features (#204 evac + #213 fire bans / drought / boil-water
  // / SoLE / cyclones) — authoritative ambient context; no incident fly, does NOT set
  // incidentPoint (none is an actionable incident). The evacuation category keeps its
  // bespoke population/homes panel; every other category uses the generic body. The
  // panel kind is chosen by category so the renderer stays fully config-driven.
  const handleSelectArcgisFeature = useCallback((f: ArcgisFeature | null) => {
    if (!f) {
      setSelected(null);
      return;
    }
    setSelected(
      f.properties.category === 'evacuation'
        ? { kind: 'evacuationOrder', feature: f }
        : { kind: 'arcgisFeature', feature: f },
    );
  }, []);
  // Estimated Comms Degradation (#109) — zone selection (ambient estimate; no
  // incident fly, does NOT set incidentPoint — it isn't an actionable incident).
  const handleSelectCommsDegradationZone = useCallback((z: CommsDegradationZoneFeature | null) => {
    setSelected(z ? { kind: 'commsDegradationZone', feature: z } : null);
  }, []);
  // E.W.0.5 — open the co-located F.1-scored hotspot from a raw hotspot panel.
  // Mirrors the #hotspot deep-link path: fetch the fused detail + fly to it.
  const handleOpenFusedHotspot = useCallback((id: string) => {
    void fetch(`/api/high-risk-hotspots/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: HighRiskHotspotFeature | null) => {
        if (!feat || (feat as { type?: string }).type !== 'Feature') return;
        setSelected({ kind: 'highRiskHotspot', feature: feat });
        const [lon, lat] = feat.geometry.coordinates;
        setIncidentPoint({ lat, lon });
        flyControllerRef.current?.flyToIncident(lat, lon);
        logAction('zoom_to_incident', {
          entity_id: feat.properties.id,
          entity_type: 'high_risk_hotspot',
        });
      })
      .catch(() => undefined);
  }, []);
  // L.2.2 — marine vessel / drone coverage / smoke_detected anomaly selections.
  const handleSelectMarineVessel = useCallback((v: VesselFeature | null) => {
    setSelected(v ? { kind: 'marineVessel', feature: v } : null);
  }, []);
  const handleSelectDroneCoverage = useCallback((d: DroneCoverageFeature | null) => {
    setSelected(d ? { kind: 'droneCoverage', feature: d } : null);
  }, []);
  // #322 — open a mission's lifecycle + live-feed panel from the missions panel.
  const handleSelectDroneMission = useCallback((m: DroneMissionView) => {
    setSelected({ kind: 'mission', mission: m });
  }, []);
  // #379 — open an OUTBOUND agency task (requester view) from the Coordination → Tasks
  // worklist: the requester sees status + the Cancel control + the target's response.
  const handleSelectAgencyTask = useCallback((task: AgencyTaskView) => {
    setSelected({ kind: 'agencyTask', task, viewerRole: 'requester' });
  }, []);
  // #379 (Phase 3b-2b) — open an INBOX agency task (target view) from the Coordination →
  // Inbox worklist: the tasked agency drives acknowledge/accept/decline/start/complete.
  const handleSelectInboxTask = useCallback((task: AgencyTaskView) => {
    setSelected({ kind: 'agencyTask', task, viewerRole: 'target' });
  }, []);
  // #387 — open a tracked incident's record from the Coordination → Incidents
  // worklist (the {kind:'incident'} SidePanel arm: operational periods + SITREP
  // chain). A "context" selection — it does NOT auto-fly (the row's "Fly to" button
  // does that), mirroring the fire / hotspot click model.
  const handleSelectIncident = useCallback((inc: Incident) => {
    setSelected({
      kind: 'incident',
      incidentId: inc.id,
      lat: inc.lat,
      lon: inc.lon,
      // Coordination re-arch Phase 1 — carry the source provenance so the detail
      // renders the full work-surface bound to the incident's source alert (IMS
      // Ruling 1). Null for a manually-declared incident → incident-level only.
      sourceRecordType: (inc.source_record_type as IncidentSourceRecordType | null) ?? null,
      sourceRecordId: inc.source_record_id ?? null,
    });
  }, []);
  // Deep-link + in-app navigation: #incident=<uuid> opens a tracked incident's record
  // (the {kind:'incident'} SidePanel arm — operational periods + SITREP chain; for an
  // Area Command parent, its grouped-incident roster). Runs on mount (shareable links)
  // AND on hashchange, so an in-app link clicks through to the child incident — an Area
  // Command SITREP member row in the viewer OR a child row in the parent panel (R5
  // Slice 4, #514). Closes any open SITREP modal so the child opens in front, not behind.
  useEffect(() => {
    const open = (): void => {
      const m = /[#&]incident=([0-9a-fA-F-]{36})/.exec(window.location.hash);
      if (!m) return;
      const incidentId = m[1]!;
      void fetch(`/api/incidents/${incidentId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((inc: Incident | null) => {
          if (!inc?.id) return;
          setActiveSitrep(null);
          handleSelectIncident(inc);
          if (inc.lat != null && inc.lon != null) {
            flyControllerRef.current?.flyToIncident(inc.lat, inc.lon);
          }
        })
        .catch(() => undefined);
    };
    open();
    window.addEventListener('hashchange', open);
    return () => window.removeEventListener('hashchange', open);
  }, [handleSelectIncident]);
  const handleSelectSmokeAnomaly = useCallback((s: SmokeAnomalyFeature | null) => {
    setSelected(s ? { kind: 'smokeDetectionAnomaly', feature: s } : null);
    if (s) {
      const [lon, lat] = s.geometry.coordinates;
      setIncidentPoint({ lat, lon });
    }
  }, []);
  // Ice Storm Fusion Anomaly (§4.5): a queue incident — select + set the incident
  // point so dispatch ETAs anchor on the outage cluster.
  const handleSelectIceAnomaly = useCallback((i: IceStormAnomalyFeature | null) => {
    setSelected(i ? { kind: 'iceStormAnomaly', feature: i } : null);
    if (i) {
      const [lon, lat] = i.geometry.coordinates;
      setIncidentPoint({ lat, lon });
    }
  }, []);
  const handleIceAnomalyCount = useCallback((n: number) => {
    setIceAnomalyCount(n);
  }, []);
  // Flood Fusion Anomaly (§4.4): a queue incident — select + set the incident point
  // so dispatch ETAs anchor on the rising gauge.
  const handleSelectFloodAnomaly = useCallback((f: FloodAnomalyFeature | null) => {
    setSelected(f ? { kind: 'floodAnomaly', feature: f } : null);
    if (f) {
      const [lon, lat] = f.geometry.coordinates;
      setIncidentPoint({ lat, lon });
    }
  }, []);
  const handleFloodAnomalyCount = useCallback((n: number) => {
    setFloodAnomalyCount(n);
  }, []);
  // #184 — media-signal igniter spike: a "needs assessment" queue item; select +
  // anchor the incident point on the cluster centroid.
  const handleSelectMediaSignalAnomaly = useCallback((m: MediaSignalAnomalyFeature | null) => {
    setSelected(m ? { kind: 'mediaSignalAnomaly', feature: m } : null);
    if (m) {
      const [lon, lat] = m.geometry.coordinates;
      setIncidentPoint({ lat, lon });
    }
  }, []);
  // Hydrometric gauge (ambient Flood Risk layer) — select only (no incident anchor).
  const handleSelectHydrometricGauge = useCallback((g: HydrometricGaugeFeature | null) => {
    setSelected(g ? { kind: 'hydrometricGauge', feature: g } : null);
  }, []);
  // After a flood acknowledge/dismiss: close the panel + refetch the queue.
  const handleFloodResolved = useCallback(() => {
    setSelected(null);
    setQueueRefreshNonce((n) => n + 1);
  }, []);
  // After a smoke confirm/dismiss: close the panel + refetch the queue (a confirm
  // adds a new hotspot; a dismiss removes the smoke item — both auto-reflect).
  const handleSmokeResolved = useCallback(() => {
    setSelected(null);
    setQueueRefreshNonce((n) => n + 1);
  }, []);
  // After an ice-storm acknowledge/dismiss: close the panel + refetch the queue
  // (acknowledge demotes the row; dismiss removes it — both auto-reflect on refetch).
  const handleIceResolved = useCallback(() => {
    setSelected(null);
    setQueueRefreshNonce((n) => n + 1);
  }, []);
  // #184 — after a media-signal confirm/dismiss: refetch the queue; a dismiss also
  // closes the panel (a confirmed spike stays open — the operator keeps watching).
  const handleMediaSignalResolved = useCallback((outcome: 'confirmed' | 'dismissed') => {
    if (outcome === 'dismissed') setSelected(null);
    setQueueRefreshNonce((n) => n + 1);
  }, []);
  // The flyToIncident reused by the Anomaly Queue rows (R.3 controller).
  const flyToIncident = useCallback((lat: number, lon: number) => {
    flyControllerRef.current?.flyToIncident(lat, lon);
  }, []);

  // #179 — toggle the operator 3D flythrough from an incident/community panel.
  // Starting captures the prior view; ending (complete or operator stop)
  // restores it and releases the on-demand photoreal tiles. Both edges audit
  // (#235, migration 0067): start_flythrough on launch, stop_flythrough exactly
  // once when the tour ends — payload.reason says how ('stopped' = operator
  // End, 'completed' = full orbit, 'destroyed' = viewer unmount).
  const toggleFlythrough = useCallback((target: FlythroughTarget) => {
    const ctl = flythroughControllerRef.current;
    if (!ctl) return;
    if (ctl.isActive()) {
      ctl.stop(); // onEnded (below) flips the button state back + logs the stop
      return;
    }
    setFlythroughActive(true);
    logAction('start_flythrough', { lon: target.lon, lat: target.lat, label: target.label });
    ctl.start(target, (reason) => {
      setFlythroughActive(false);
      logAction('stop_flythrough', {
        lon: target.lon,
        lat: target.lat,
        label: target.label,
        reason,
      });
    });
  }, []);

  // #180 — enter the AAR time replay from an incident panel. The replay data
  // is a one-shot read-model fetch; the map controller captures the prior view
  // and the overlay drives scrubbing. While open, the live urgent-alert layers
  // are suppressed (see the MapView props) under the overlay's NOT-LIVE banner.
  // Both edges audit (#240, migration 0070, the flythrough-pair pattern):
  // start_aar_replay on a successful open, stop_aar_replay exactly once when
  // the replay ends — payload.reason says how ('stopped' = operator exit,
  // 'destroyed' = viewer unmount). The incident_id key scopes both rows back
  // into this incident's own future replays.
  const openAarReplay = useCallback(async (incidentId: string) => {
    const ctl = aarReplayControllerRef.current;
    if (!ctl || ctl.isActive()) return;
    try {
      const data = await fetchIncidentReplay(incidentId);
      setAarReplayData(data);
      logAction('start_aar_replay', {
        incident_id: incidentId,
        incident_number: data.incident.incident_number,
      });
      ctl.start(data, (reason) => {
        setAarReplayData(null);
        logAction('stop_aar_replay', {
          incident_id: incidentId,
          incident_number: data.incident.incident_number,
          reason,
        });
      });
    } catch (err) {
      console.error('AAR replay failed to load', err);
    }
  }, []);

  const exitAarReplay = useCallback(() => {
    // stop() flies back to the captured view; onEnded clears the overlay state.
    aarReplayControllerRef.current?.stop();
  }, []);

  // §4.4 — open the incident-scoped Evacuation Logistics panel (used by the
  // community link, the SITL surface, and the #206 evac-order cross-link).
  const handleOpenEvacuation = useCallback((incidentId: string) => {
    setSelected({ kind: 'evacuation', incidentId });
  }, []);

  // #197 — selection bridge: the operations panel drives the COP. A queue row
  // clicked on `/panel` broadcasts a compact selection; here the COP opens it
  // exactly as a click would. Camera + fused hotspot reuse their fetch-by-id +
  // open handlers; the other Urgent-Alert sources fly the map to the point (a
  // per-source single-id detail endpoint would let those open the rail too).
  const handleBridgeSelection = useCallback(
    (sel: BridgeSelection) => {
      if (sel.kind === 'camera') {
        void fetch(`/api/cameras/${sel.id}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((feat: CameraFeature | null) => {
            if (feat && (feat as { type?: string }).type === 'Feature') focusCamera(feat);
          })
          .catch(() => undefined);
        return;
      }
      if (sel.kind === 'hotspot') {
        handleOpenFusedHotspot(sel.id);
        return;
      }
      // #206 cross-link — open the evac order panel by id (the detail carries the
      // geometry the body needs), then fly. Mirrors the fused-hotspot open path.
      if (sel.kind === 'evacuationOrder') {
        void fetch(`/api/arcgis-features/${sel.id}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((feat: ArcgisFeature | null) => {
            if (feat && (feat as { type?: string }).type === 'Feature') {
              handleSelectArcgisFeature(feat);
            }
          })
          .catch(() => undefined);
        if (sel.lat != null && sel.lon != null) {
          flyControllerRef.current?.flyToIncident(sel.lat, sel.lon);
        }
        return;
      }
      // #206 — open the incident-scoped Evacuation Logistics panel (§4.4).
      if (sel.kind === 'evacuation') {
        handleOpenEvacuation(sel.id);
        if (sel.lat != null && sel.lon != null) {
          flyControllerRef.current?.flyToIncident(sel.lat, sel.lon);
        }
        return;
      }
      // #454 — a `community` selection OPENS the community record (which mounts the #420a
      // coordination thread), honouring the bridge's own contract ("opens that entity", not just
      // a recenter). This is what makes the multi-community coordination-inbox row a REAL jump to
      // the thread (and upgrades the #206 evac-link community jump from recenter-only). Fly at
      // once when the selection carried coords (the evac-link jump does); else fly to the fetched
      // centroid. The feature is built INLINE (mirrors communityToFeature, which is defined later
      // and would use-before-init here) — the CommunityFeatureProps shape is compile-checked.
      if (sel.kind === 'community') {
        if (sel.lat != null && sel.lon != null) {
          flyControllerRef.current?.flyToIncident(sel.lat, sel.lon);
        }
        void fetchCommunityDetail(sel.id)
          .then((d) => {
            setSelected({
              kind: 'community',
              feature: {
                type: 'Feature',
                id: d.id,
                geometry: { type: 'Point', coordinates: [d.lon, d.lat] },
                properties: {
                  id: d.id,
                  name: d.name,
                  community_type: d.community_type,
                  jurisdiction: d.jurisdiction,
                  population: d.population,
                  vulnerable_population: d.vulnerable_population,
                  latest_wellness_status: null,
                  latest_headcount_summary: null,
                  cimd_situational_vulnerability_q: d.cimd_situational_vulnerability_q,
                },
              },
            });
            if (sel.lat == null || sel.lon == null) {
              flyControllerRef.current?.flyToIncident(d.lat, d.lon);
            }
          })
          .catch(() => undefined);
        return;
      }
      // incident (and any future point kind) — fly to the centroid.
      if (sel.lat != null && sel.lon != null) {
        flyControllerRef.current?.flyToIncident(sel.lat, sel.lon);
      }
    },
    [focusCamera, handleOpenFusedHotspot, handleSelectArcgisFeature, handleOpenEvacuation],
  );
  useSelectionBridge(handleBridgeSelection);

  // #149 home view — fly to the saved vantage, falling back to the all-Canada
  // default (#138) when the operator hasn't set one. loadHomeView() reads the
  // localStorage mirror, which the #155 server sync keeps in lockstep (server
  // wins), so this stays a synchronous, offline-safe read.
  const flyHome = useCallback(() => {
    const ctrl = flyControllerRef.current;
    if (!ctrl) return;
    const home = loadHomeView();
    if (home) {
      void ctrl.flyToView(home);
    } else {
      const { west, south, east, north } = CANADA_VIEW_BOUNDS;
      void ctrl.flyToBounds(west, south, east, north);
    }
  }, []);
  // Phase 6 — "National view": ALWAYS frame all of Canada, ignoring any saved
  // home (the deliberate distinction from H/flyHome). Stateless camera action —
  // manual only, no auto-recenter — delegating to the home-agnostic
  // flyNationalView helper (pinned by nationalView.test.ts). Bound to the
  // command-bar National button and ⇧H.
  const flyNational = useCallback(() => {
    flyNationalView(flyControllerRef.current);
  }, []);
  // #149 — "Set as my home view": capture the current vantage; brief confirmation.
  // #155 — persist to the server (mirrored to localStorage) so home follows the
  // operator cross-device.
  const [homeJustSet, setHomeJustSet] = useState(false);
  const homeConfirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setHome = useCallback(() => {
    const ctrl = flyControllerRef.current;
    if (!ctrl) return;
    void persistHomeView(ctrl.getView());
    logAction('save_home_view', {});
    setHomeJustSet(true);
    if (homeConfirmTimer.current) clearTimeout(homeConfirmTimer.current);
    homeConfirmTimer.current = setTimeout(() => setHomeJustSet(false), 1600);
  }, []);

  // #154 — operator-saved named regions (fly-to + SITREP scope). Loaded from the
  // server on mount; the home view is reconciled there too (server wins).
  // #187 — the operator's custom seat profiles ride the same fetch.
  const [savedRegions, setSavedRegions] = useState<SavedRegion[]>([]);
  const [seatProfiles, setSeatProfiles] = useState<SavedSeatProfile[]>([]);
  useEffect(() => {
    const ac = new AbortController();
    void fetchSavedViews(ac.signal).then((data) => {
      if (!data) return;
      setSavedRegions(data.regions);
      setSeatProfiles(data.profiles);
      if (data.home) {
        // Server wins: mirror it locally so flyHome / first-paint use it, and —
        // on a fresh device with no deep-link in the URL — fly to it now (the
        // initial frame went to Canada because localStorage was empty).
        const hadLocal = loadHomeView();
        persistHomeView(data.home).catch(() => undefined); // mirror only (already on server)
        if (!hadLocal && !hasDeepLinkInHash()) {
          flyControllerRef.current?.flyToView(data.home);
        }
      }
    });
    return () => ac.abort();
  }, []);

  // #154 — capture the current map extent as a named region (server-persisted).
  const handleSaveRegion = useCallback(
    (label: string) => {
      const bbox = currentBbox;
      if (!bbox) {
        showToast('Map view not ready yet — pan or zoom once, then retry.', { variant: 'error' });
        return;
      }
      void persistRegion(label, bbox).then((region) => {
        if (!region) {
          showToast('Could not save that region. Please retry.', { variant: 'error' });
          return;
        }
        logAction('save_region_view', { region_id: region.id, label: region.label });
        // Replace a same-named region (the server replaces case-insensitively).
        setSavedRegions((prev) => [
          region,
          ...prev.filter((r) => r.label.toLowerCase() !== region.label.toLowerCase()),
        ]);
        showToast(`Region “${region.label}” saved.`, { variant: 'success' });
      });
    },
    [currentBbox],
  );

  // #154 — fly the camera to a saved region's extent (the Google-familiar zoom,
  // reusing the search-surface flyToBounds path).
  const handleFlyRegion = useCallback((region: SavedRegion) => {
    logAction('fly_to_saved_view', { region_id: region.id, label: region.label });
    const { west, south, east, north } = region.bbox;
    flyControllerRef.current?.flyToBounds(west, south, east, north);
  }, []);

  // #154 — delete a saved region.
  const handleDeleteRegion = useCallback((region: SavedRegion) => {
    void deleteSavedView(region.id).then((ok) => {
      if (!ok) {
        showToast('Could not delete that region. Please retry.', { variant: 'error' });
        return;
      }
      logAction('delete_saved_view', { region_id: region.id, label: region.label });
      setSavedRegions((prev) => prev.filter((r) => r.id !== region.id));
    });
  }, []);

  // #187 — apply a seat profile: locked defaults + the profile's layer deltas
  // (never current + deltas, so seat switches don't compound), flowing through
  // the normal prefs path (localStorage effect + debounced server PUT). A saved
  // profile's panel arrangement also restores the pinned-feed set across every
  // open window. Audited under apply_seat_profile (migration 0066).
  const handleApplySeatProfile = useCallback((target: SeatProfileTarget) => {
    setPrefs((prev) => {
      const next = applyProfileLayerDeltas(prev, target.view.layerDeltas);
      // #458 — a profile applied during the transient lens is part of the glance,
      // discarded on exit; don't persist it to the server.
      if (!transientViewActiveRef.current) queueServerLayerSync(next, prev);
      return next;
    });
    if (target.view.panelArrangement) {
      replaceAllPins(target.view.panelArrangement.pinnedFeeds);
    }
    logAction('apply_seat_profile', {
      profile_id: target.id,
      label: target.label,
      seed: target.isSeed,
    });
    showToast(`Seat profile “${target.label}” applied.`, { variant: 'success' });
  }, []);

  // #187 — save the current workspace (layer deltas over the locked defaults +
  // the pinned-feed set) as a named profile. Replaces a same-named profile
  // (the server replaces case-insensitively, like regions).
  const handleSaveSeatProfile = useCallback(
    (label: string) => {
      const view = snapshotProfileView(prefs, loadPins());
      void persistSeatProfile(label, view).then((profile) => {
        if (!profile) {
          showToast('Could not save that profile. Please retry.', { variant: 'error' });
          return;
        }
        logAction('save_seat_profile', { profile_id: profile.id, label: profile.label });
        setSeatProfiles((prev) => [
          profile,
          ...prev.filter((p) => p.label.toLowerCase() !== profile.label.toLowerCase()),
        ]);
        showToast(`Seat profile “${profile.label}” saved.`, { variant: 'success' });
      });
    },
    [prefs],
  );

  // #458 (role-view council §3) — enter the PEOC "view as <community>" TRANSIENT
  // lens: snapshot the operator's REAL prefs + current map extent, apply the
  // Municipal-EOC layer view, and fly to the community — a glance at what that
  // community's operator sees before tasking / briefing. NON-PERSISTENT by
  // construction: setPrefs directly (never queueServerLayerSync / apply_seat_profile),
  // and the localStorage write is suppressed while transientViewActiveRef is up.
  // A data-driven LENS, never a mode — no role render-branch (council §5.2).
  const handleViewAsCommunity = useCallback(
    (community: CommunityFeature) => {
      if (transientViewActiveRef.current) return; // already glancing — ignore
      const { snapshot, lens } = enterMunicipalLens(prefs);
      transientSnapshotRef.current = snapshot;
      transientMapViewRef.current = flyControllerRef.current?.getView() ?? null;
      transientViewActiveRef.current = true;
      setPrefs(lens);
      setViewAsCommunity(community);
      const [lon, lat] = community.geometry.coordinates;
      void flyControllerRef.current?.flyToIncident(lat, lon);
    },
    [prefs],
  );

  // #458 — exit the lens: restore EXACTLY the snapshot (never re-derive from the
  // bare default — that would drop the operator's own customizations) + the prior
  // map extent. Flip the ref OFF first so the restored real prefs persist normally.
  const exitViewAsCommunity = useCallback(() => {
    if (!transientViewActiveRef.current) return;
    const snapshot = transientSnapshotRef.current;
    const mapView = transientMapViewRef.current;
    transientViewActiveRef.current = false;
    transientSnapshotRef.current = null;
    transientMapViewRef.current = null;
    setViewAsCommunity(null);
    if (snapshot) setPrefs(exitMunicipalLens(snapshot));
    if (mapView) void flyControllerRef.current?.flyToView(mapView);
  }, []);

  // #458 — Esc dismisses the transient lens (mirrors the modal-dismiss idiom).
  useEffect(() => {
    if (!viewAsCommunity) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') exitViewAsCommunity();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewAsCommunity, exitViewAsCommunity]);

  // #187 — delete a custom saved profile (the curated seeds are code constants
  // and cannot be deleted).
  const handleDeleteSeatProfile = useCallback((profile: SavedSeatProfile) => {
    void deleteSavedView(profile.id).then((ok) => {
      if (!ok) {
        showToast('Could not delete that profile. Please retry.', { variant: 'error' });
        return;
      }
      logAction('delete_seat_profile', { profile_id: profile.id, label: profile.label });
      setSeatProfiles((prev) => prev.filter((p) => p.id !== profile.id));
    });
  }, []);

  // §4.4 — open the incident-scoped Evacuation Logistics panel (surfaced from the
  // community / fire / hotspot detail). Swaps the side panel to the evac surface.
  // #185 — Alert → ASSIST cross-link: open the full assist request in a modal
  // (view_assistance_request is audited by the detail on mount; no new verb).
  const openConnectedAssist = useCallback((requestId: string) => {
    void fetchAssistanceRequest(requestId)
      .then(setConnectedAssist)
      .catch(() => showToast('Could not load that assistance request.', { variant: 'error' }));
  }, []);

  // W.2 — fetch a community's detail and fold its wellness status + headcount chip
  // into the map status cache (drives marker colour + the regional-zoom badge).
  const loadCommunityInfo = useCallback((id: string) => {
    void fetchCommunityDetail(id)
      .then((d) => {
        const badge = headcountBadge(d.name, d.latest_headcount, d.population);
        setCommunityStatuses((prev) => ({
          ...prev,
          [id]: { status: d.latest_wellness_check?.status ?? null, badge },
        }));
      })
      .catch(() => undefined);
  }, []);

  // W.2.1 (Task #91) — communities now paint coloured on first paint from the
  // list endpoint's latest_wellness_status + latest_headcount_summary, so we no
  // longer fetch each in-view community's detail just to colour the map. The
  // status cache is still used as a *fresher* override after an operator edits a
  // wellness check / headcount (handleCommunityChanged below). No-op on in-view.
  const handleCommunitiesInView = useCallback((_ids: string[]) => {}, []);

  const handleSelectCommunity = useCallback((c: CommunityFeature | null) => {
    setSelected(c ? { kind: 'community', feature: c } : null);
  }, []);

  /* ---------------- W.2 — community map editor (declare / move / edit / retire) -- */

  // Build a light CommunityFeature from an API Community (for selecting a freshly
  // declared / moved community). Wellness/headcount fold in via the status cache.
  const communityToFeature = useCallback(
    (c: Community): CommunityFeature => ({
      type: 'Feature',
      id: c.id,
      geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
      properties: {
        id: c.id,
        name: c.name,
        community_type: c.community_type,
        jurisdiction: c.jurisdiction,
        population: c.population,
        vulnerable_population: c.vulnerable_population,
        latest_wellness_status: null,
        latest_headcount_summary: null,
        cimd_situational_vulnerability_q: c.cimd_situational_vulnerability_q,
      },
    }),
    [],
  );

  // Ensure the community layer is visible so the operator sees what they declare /
  // move (turn on the Flood-Vulnerable toggle if both community toggles are off).
  const ensureCommunitiesVisible = useCallback(() => {
    setPrefs((prev) =>
      prev.floodVulnerableCommunities || prev.iceStormVulnerableCommunities
        ? prev
        : { ...prev, floodVulnerableCommunities: true },
    );
  }, []);

  // Toolbar "Declare Community": start the place-a-point tool in declare mode.
  const startDeclareCommunity = useCallback(() => {
    if (placingCommunity) {
      communityControllerRef.current?.cancelPlace();
      return;
    }
    ensureCommunitiesVisible();
    placeIntentRef.current = { mode: 'declare' };
    communityControllerRef.current?.startPlace();
  }, [placingCommunity, ensureCommunitiesVisible]);

  // From the community panel "Move on map": re-place this community's centroid.
  const startMoveCommunity = useCallback((community: CommunityFeature) => {
    placeIntentRef.current = { mode: 'move', community };
    communityControllerRef.current?.startPlace();
    showToast('Click the map to set the new location (Esc to cancel).', { variant: 'info' });
  }, []);

  // The place tool started/stopped (incl. Esc / right-click cancel) — sync the UI.
  const handleCommunityPlaceStateChange = useCallback((active: boolean) => {
    setPlacingCommunity(active);
  }, []);

  // A point was placed — declare a new community (collect details first) or commit
  // a centroid move per the active intent.
  const handleCommunityPlaced = useCallback(
    ([lon, lat]: [number, number]) => {
      const intent = placeIntentRef.current;
      placeIntentRef.current = null;
      if (!intent) return;
      if (intent.mode === 'declare') {
        setPendingDeclare({ lon, lat });
        return;
      }
      // Move: a geography-only PATCH (audited move_community).
      void patchCommunity(intent.community.id, { lon, lat, session_id: getSessionId() })
        .then((updated) => {
          showToast(`${updated.name} moved.`, { variant: 'success' });
          communityControllerRef.current?.refreshCommunities();
          loadCommunityInfo(updated.id);
          setSelected({ kind: 'community', feature: communityToFeature(updated) });
        })
        .catch(() =>
          showToast('Could not move the community. Please retry.', { variant: 'error' }),
        );
    },
    [communityToFeature, loadCommunityInfo],
  );

  // The declare form was submitted — refresh the layer + select the new community.
  const handleCommunityDeclared = useCallback(
    (community: Community) => {
      setPendingDeclare(null);
      communityControllerRef.current?.refreshCommunities();
      loadCommunityInfo(community.id);
      setSelected({ kind: 'community', feature: communityToFeature(community) });
    },
    [communityToFeature, loadCommunityInfo],
  );

  // After an identity/population edit: refetch the layer (the marker name changed)
  // + refresh the status/badge cache; the panel refetches its own detail.
  const handleCommunityEntityChanged = useCallback(
    (id: string) => {
      communityControllerRef.current?.refreshCommunities();
      loadCommunityInfo(id);
    },
    [loadCommunityInfo],
  );

  // After a soft-retire: refresh the layer (the marker drops out) + close the panel.
  const handleCommunityRetired = useCallback((_id: string) => {
    communityControllerRef.current?.refreshCommunities();
    setSelected(null);
  }, []);

  /* ---------------- #139 / #141 — unified search dispatch ---------------- */

  // A geocoded PLACE was chosen: fly to its extent (the Google-familiar zoom).
  // bbox-less results (e.g. a street centroid) fall back to a point incident zoom.
  const handleDispatchPlace = useCallback((place: SearchPlace) => {
    logAction('search_and_fly', {
      result_type: 'place',
      title: place.title,
      has_bbox: place.bbox != null,
    });
    const ctrl = flyControllerRef.current;
    if (place.bbox) ctrl?.flyToBounds(place.bbox[0], place.bbox[1], place.bbox[2], place.bbox[3]);
    else ctrl?.flyToIncident(place.lat, place.lon);
  }, []);

  // A map ENTITY was chosen: route through the EXISTING deep-link handlers so the
  // side panel / overlay opens exactly as a click or #deep-link would.
  const handleDispatchEntity = useCallback(
    (entity: SearchEntity) => {
      logAction('search_and_fly', {
        result_type: 'entity',
        entity_kind: entity.kind,
        entity_id: entity.id,
      });
      switch (entity.kind) {
        case 'camera':
          void fetch(`/api/cameras/${entity.id}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((feat: CameraFeature | null) => {
              if (feat && (feat as { type?: string }).type === 'Feature') focusCamera(feat);
            })
            .catch(() => undefined);
          break;
        case 'response_asset':
          void fetch(`/api/response-assets/${entity.id}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((feat: (ResponseAssetFeature & { geometry: unknown }) | null) => {
              if (feat && (feat as { type?: string }).type === 'Feature' && feat.geometry) {
                setSelected({ kind: 'asset', feature: feat });
                flyControllerRef.current?.flyToIncident(entity.lat, entity.lon);
              }
            })
            .catch(() => undefined);
          break;
        case 'active_fire':
          void fetch(`/api/active-fires/${entity.id}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((feat: ActiveFireFeature | null) => {
              if (!feat || (feat as { type?: string }).type !== 'Feature') return;
              setSelected({ kind: 'fire', feature: feat });
              const [lon, lat] = feat.properties.centroid;
              flyControllerRef.current?.flyToIncident(lat, lon);
            })
            .catch(() => undefined);
          break;
        case 'community':
          void fetchCommunityDetail(entity.id)
            .then((d) => {
              handleSelectCommunity(communityToFeature(d));
              loadCommunityInfo(d.id);
              flyControllerRef.current?.flyToIncident(d.lat, d.lon);
            })
            .catch(() => undefined);
          break;
        case 'incident':
          // No standalone incident panel — the SITL incident surface lives inside
          // the originating fire's panel. Open that fire when the incident was
          // promoted from one; otherwise just fly to the centroid.
          if (entity.source_ref?.kind === 'active_fire') {
            const fireId = entity.source_ref.id;
            void fetch(`/api/active-fires/${fireId}`)
              .then((r) => (r.ok ? r.json() : null))
              .then((feat: ActiveFireFeature | null) => {
                if (feat && (feat as { type?: string }).type === 'Feature') {
                  setSelected({ kind: 'fire', feature: feat });
                  const [lon, lat] = feat.properties.centroid;
                  flyControllerRef.current?.flyToIncident(lat, lon);
                  return;
                }
                flyControllerRef.current?.flyToIncident(entity.lat, entity.lon);
              })
              .catch(() => flyControllerRef.current?.flyToIncident(entity.lat, entity.lon));
          } else {
            flyControllerRef.current?.flyToIncident(entity.lat, entity.lon);
          }
          break;
      }
    },
    [focusCamera, handleSelectCommunity, communityToFeature, loadCommunityInfo],
  );

  // R.4.2 — staging-area marker selection.
  const handleSelectStagingArea = useCallback((s: StagingArea | null) => {
    setSelected(s ? { kind: 'stagingArea', area: s } : null);
  }, []);
  // After a staging area is closed: clear the panel. The ambient marker clears on
  // the next map refresh (closed areas drop out of the open-only bbox fetch).
  const handleStagingAreaChanged = useCallback(() => {
    setSelected(null);
  }, []);

  /* ---------------- Task #64 — sectoring overlay ---------------- */
  const handleSelectSector = useCallback((s: Sector | null) => {
    setSelected(s ? { kind: 'sector', sector: s } : null);
  }, []);
  // After a sector metadata/close edit: refresh the map layer + keep the panel in
  // sync with the updated row.
  const handleSectorChanged = useCallback((updated?: Sector) => {
    sectorControllerRef.current?.refreshSectors();
    if (updated) setSelected({ kind: 'sector', sector: updated });
  }, []);

  /** Ensure the current selection has a real tracked incident to attach a sector
   *  to — resolve it, else promote the source record (idempotent server-side). */
  const ensureIncidentId = useCallback(async (): Promise<string | null> => {
    if (selectedIncidentId) return selectedIncidentId;
    if (!sitrepIncident) return null;
    const inc = await createIncident({
      session_id: getSessionId(),
      name: sitrepIncident.label,
      hazard_type: 'wildfire',
      source_record_type:
        sitrepIncident.type === 'active_fire' ? 'active_wildland_fire' : 'high_risk_hotspot',
      source_record_id: sitrepIncident.id,
    });
    setSelectedIncidentId(inc.id);
    return inc.id;
  }, [selectedIncidentId, sitrepIncident]);

  // "Draw Sector" — promote-if-needed, then start the polygon draw tool.
  const startSectorDraw = useCallback(() => {
    if (drawingSector) {
      sectorControllerRef.current?.cancelDraw();
      sectorDrawTargetRef.current = null;
      setDrawingSector(false);
      return;
    }
    void ensureIncidentId()
      .then((incidentId) => {
        if (!incidentId) {
          showToast('Select an incident first to draw a sector.', { variant: 'error' });
          return;
        }
        sectorDrawTargetRef.current = { mode: 'create', incidentId };
        setDrawingSector(true);
        sectorControllerRef.current?.startDraw();
      })
      .catch((err) =>
        showToast((err as Error).message || 'Could not start a sector.', { variant: 'error' }),
      );
  }, [drawingSector, ensureIncidentId]);

  // From SectorBody "Edit Geometry": redraw THIS sector's polygon.
  const handleEditSectorGeometry = useCallback((sector: Sector) => {
    sectorDrawTargetRef.current = { mode: 'edit', sectorId: sector.id };
    setDrawingSector(true);
    sectorControllerRef.current?.startDraw();
  }, []);

  // The draw tool finished a polygon (closed ring). Create or edit per the target.
  const handleSectorDrawn = useCallback((ring: Array<[number, number]>) => {
    setDrawingSector(false);
    const target = sectorDrawTargetRef.current;
    sectorDrawTargetRef.current = null;
    if (!target) return;
    const geometry = { type: 'Polygon' as const, coordinates: [ring] };
    if (target.mode === 'edit') {
      void updateSectorGeometry(target.sectorId, { session_id: getSessionId(), geometry })
        .then((s) => {
          showToast('Sector geometry updated.', { variant: 'success' });
          sectorControllerRef.current?.refreshSectors();
          setSelected({ kind: 'sector', sector: s });
        })
        .catch((err) =>
          showToast((err as Error).message || 'Could not update geometry.', { variant: 'error' }),
        );
      return;
    }
    // Create: collect a name in the naming form before POSTing.
    setPendingSector({ ring, incidentId: target.incidentId });
    setPendingName('');
    setPendingLead('');
  }, []);

  const submitPendingSector = useCallback(() => {
    if (!pendingSector) return;
    const name = pendingName.trim() || 'Sector';
    void createSector({
      session_id: getSessionId(),
      incident_id: pendingSector.incidentId,
      name,
      geometry: { type: 'Polygon', coordinates: [pendingSector.ring] },
      lead_label: pendingLead.trim() || undefined,
    })
      .then((s) => {
        showToast(`Sector "${s.name}" drawn.`, { variant: 'success' });
        setPendingSector(null);
        sectorControllerRef.current?.refreshSectors();
        setSelected({ kind: 'sector', sector: s });
      })
      .catch((err) =>
        showToast((err as Error).message || 'Could not create sector.', { variant: 'error' }),
      );
  }, [pendingSector, pendingName, pendingLead]);

  const cancelPendingSector = useCallback(() => setPendingSector(null), []);

  // #445-(3) — operator-declared evacuation order: draw → declare → render flow.
  // Selection from a map click on the operator-evac layer.
  const handleSelectOperatorEvacOrder = useCallback((f: OperatorEvacOrderFeature | null) => {
    setSelected(f ? { kind: 'operatorEvacOrder', feature: f } : null);
  }, []);

  // From CommunityBody "Record evacuation order — issued by <name>": start the
  // evac-area draw, pre-seeding this community as the named issuing authority.
  const startEvacDraw = useCallback(
    (preseed: { communityId: string; communityName: string; communityType: string }) => {
      if (drawingEvacOrder) {
        evacuationOrderControllerRef.current?.cancelDraw();
        evacDrawPreseedRef.current = null;
        setDrawingEvacOrder(false);
        return;
      }
      evacDrawPreseedRef.current = preseed;
      setSelected(null); // close the community panel so the operator can draw freely
      setDrawingEvacOrder(true);
      evacuationOrderControllerRef.current?.startDraw();
    },
    [drawingEvacOrder],
  );

  // The evac draw tool finished a polygon — open the declare modal, mapping the
  // community type to a default issuing-authority type (operator can override).
  const handleEvacOrderDrawn = useCallback((ring: Array<[number, number]>) => {
    setDrawingEvacOrder(false);
    // #459 — geometry-CORRECTION path: the redraw is for an existing order, so PATCH its
    // area instead of opening the declare modal.
    const edit = evacGeometryEditRef.current;
    if (edit) {
      evacGeometryEditRef.current = null;
      evacuationOrderControllerRef.current?.clearPreview();
      void patchEvacuationOrderGeometry(edit.orderId, {
        session_id: getSessionId(),
        geometry: { type: 'Polygon', coordinates: [ring] },
      })
        .then((updated) => {
          showToast('Evacuation area corrected.', { variant: 'success' });
          evacuationOrderControllerRef.current?.refreshOrders();
          setSelected({ kind: 'operatorEvacOrder', feature: updated });
        })
        .catch((err) =>
          showToast((err as Error).message || 'Could not correct the area. Please retry.', {
            variant: 'error',
          }),
        );
      return;
    }
    const ctx = evacDrawPreseedRef.current;
    evacDrawPreseedRef.current = null;
    if (!ctx) return;
    // Hand-drawn ring → no geometrySource (the declare modal shows no provenance banner).
    setPendingEvacOrder({
      ring,
      preseed: {
        communityId: ctx.communityId,
        issuingAuthorityName: ctx.communityName,
        issuingAuthorityType: communityTypeToIssuingAuthorityType(ctx.communityType),
      },
    });
  }, []);

  // #457 — from CommunityBody "Record evacuation order — issued by <name>": the
  // municipal seat's PRIMARY one-click path. Generate the evacuation zone from the
  // community's OWN geometry (service_area → StatCan boundary → radius), preview it
  // on the map, and open the declare modal pre-seeded — the operator reviews /
  // adjusts / redraws before issuing (never a black box). On declare, R2/#478
  // auto-links it to a co-located open incident as its protective-action zone.
  const startCommunityEvacuation = useCallback(
    (preseed: { communityId: string; communityName: string; communityType: string }) => {
      if (evacOneClickBusyRef.current) return; // ignore a rapid second click
      evacOneClickBusyRef.current = true;
      evacDrawPreseedRef.current = preseed; // kept so "redraw manually" reuses it
      void fetchCommunityEvacuationZone(preseed.communityId)
        .then((zone) => {
          const ring = zone.geometry.coordinates[0] as Array<[number, number]>;
          evacuationOrderControllerRef.current?.previewRing(ring);
          setPendingEvacOrder({
            ring,
            preseed: {
              communityId: preseed.communityId,
              issuingAuthorityName: preseed.communityName,
              issuingAuthorityType: communityTypeToIssuingAuthorityType(preseed.communityType),
            },
            geometrySource: {
              source: zone.source,
              placeName: zone.place_name,
              radiusM: zone.radius_m,
            },
          });
        })
        .catch((err) =>
          showToast(
            (err as Error).message ||
              'Could not generate the evacuation area. Try drawing it manually.',
            { variant: 'error' },
          ),
        )
        .finally(() => {
          evacOneClickBusyRef.current = false;
        });
    },
    [],
  );

  // "Redraw the area manually" from the declare modal — clear the generated preview
  // and drop into the hand-draw tool with the same community pre-seed.
  const startEvacRedraw = useCallback(() => {
    const ctx = evacDrawPreseedRef.current;
    evacuationOrderControllerRef.current?.clearPreview();
    setPendingEvacOrder(null);
    if (ctx) startEvacDraw(ctx);
  }, [startEvacDraw]);

  // After an escalate/rescind from the detail panel: refresh the layer + reflect the
  // updated order in the panel.
  const handleOperatorEvacOrderChanged = useCallback((updated: OperatorEvacOrderFeature) => {
    evacuationOrderControllerRef.current?.refreshOrders();
    setSelected({ kind: 'operatorEvacOrder', feature: updated });
  }, []);

  // #459 — auto-enable the (default-OFF) operator-evac layer when an edit opens so the
  // operator sees the polygon it is correcting (reuses #456's post-declare auto-enable).
  const handleEnsureEvacLayerVisible = useCallback(() => {
    setPrefs((prev) => ({ ...prev, operatorEvacuationOrders: true }));
  }, []);

  // #459 — "Correct the area on the map": mark the next drawn ring as a geometry
  // correction for THIS order, turn the layer on, free the map, and start the draw tool.
  const handleCorrectEvacGeometry = useCallback((feature: OperatorEvacOrderFeature) => {
    setPrefs((prev) => ({ ...prev, operatorEvacuationOrders: true }));
    evacGeometryEditRef.current = { orderId: feature.id };
    evacDrawPreseedRef.current = null; // a correction, not a community-preseeded new order
    setSelected(null); // free the map to draw over the existing polygon
    setDrawingEvacOrder(true);
    evacuationOrderControllerRef.current?.startDraw();
  }, []);

  // After a headcount/wellness edit: refetch that community so its marker recolours.
  const handleCommunityChanged = useCallback(
    (id: string) => {
      communityFetched.current.add(id);
      loadCommunityInfo(id);
    },
    [loadCommunityInfo],
  );
  // Map the active selection to a queue-row id so the row highlights in lockstep.
  const selectedQueueId = useMemo<string | null>(() => {
    if (!selected) return null;
    if (selected.kind === 'highRiskHotspot') return `hotspot:${selected.feature.properties.id}`;
    if (selected.kind === 'capAlert') return `cap:${selected.feature.properties.id}`;
    if (selected.kind === 'smokeDetectionAnomaly') return `smoke:${selected.feature.properties.id}`;
    if (selected.kind === 'iceStormAnomaly') return `ice:${selected.feature.properties.id}`;
    if (selected.kind === 'floodAnomaly') return `flood:${selected.feature.properties.id}`;
    return null;
  }, [selected]);
  const handleFireCount = useCallback((n: number) => {
    setFireCount(n);
  }, []);
  // #424 — the rail "Alerts" badge value, emitted by the AnomalyQueuePanel as the
  // in-view urgent (RL≥3) count from its own countQueue. Stable callback so the
  // panel's emit effect doesn't re-fire on identity churn.
  const handleUrgentAlertCount = useCallback((n: number | null) => {
    setUrgentAlertCount(n);
  }, []);
  const handleAssetCounts = useCallback((counts: AssetCounts) => {
    setAssetCounts(counts);
  }, []);
  const handleAircraftCount = useCallback((n: number) => {
    setAircraftCount(n);
  }, []);
  const handleCameraCount = useCallback((count: number, loading: boolean) => {
    setCameraCount(count);
    setLoadingCameras(loading);
  }, []);
  const handleSensorCount = useCallback((count: number, loading: boolean) => {
    setSensorCount(count);
    setLoadingSensors(loading);
  }, []);

  // Esc closes the side panel when no overlay is open (the overlay handles its
  // own Esc → closeOverlay, which also clears the panel).
  useEffect(() => {
    if (overlayCamera || !selected) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayCamera, selected]);

  // #149 + Phase 6 — global "H" flies to the saved home view (Canada default
  // when unset); "⇧H" flies to the all-Canada National view, ignoring any saved
  // home. Shift is the National modifier (not a guard-break): meta/ctrl/alt are
  // still ignored, and the keybind is suppressed while typing in a field, so it
  // never fights search / forms. One listener handles both branches.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'h' && e.key !== 'H') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      e.preventDefault();
      if (e.shiftKey) flyNational();
      else flyHome();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flyHome, flyNational]);

  return (
    <div className="relative flex h-full w-full">
      {/* B.1 — view-mode switch [ Operator | Briefing ] (operator-cop-doctrine
          §3B.7) and the #139/#141 unified search now live in the command bar
          (MODE group + leading Search group, operator mode); BriefingView
          carries its own "← Operator view" exit. */}
      {prefs.briefingMode ? (
        <BriefingView
          bbox={currentBbox}
          readOnly={false}
          onExitBriefing={() => setBriefingMode(false)}
        />
      ) : (
        <>
          <LeftRail
            /* #325 — UNKNOWN (→ "—"), not a forced/frozen "0", whenever /api is unreachable.
           The panelOpen branch would otherwise hard-code 0, and while the ops panel is
           open the rail's AnomalyQueuePanel is unmounted so it can't emit null itself —
           so we gate on the LIVE reachability signal here, not on the (frozen) emitted
           count. Reachable path is byte-unchanged (panelOpen ? 0 : count). */
            anomalyCount={!apiHealth.reachable ? null : panelOpen ? 0 : urgentAlertCount}
            queue={
              panelOpen ? (
                // #197 two-queue guard — the Urgent Alert queue lives on the open
                // operations panel now, never in both places at once.
                <div
                  data-testid="queue-on-panel-notice"
                  className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center"
                >
                  <p className="text-sm text-slate-300">
                    Urgent Alerts are showing on the Operations Panel.
                  </p>
                  <button
                    type="button"
                    onClick={openOperationsPanel}
                    className="rounded border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
                  >
                    Focus Operations Panel
                  </button>
                  <p className="text-[11px] text-slate-500">
                    Close the panel window to bring alerts back here.
                  </p>
                </div>
              ) : (
                <AnomalyQueuePanel
                  bbox={currentBbox}
                  visible={prefs.urgentAnomalies}
                  capVisible={prefs.urgentAnomalies && prefs.extremeWeatherWarnings}
                  selectedId={selectedQueueId}
                  refreshNonce={queueRefreshNonce}
                  mutedClasses={[
                    ...(prefs.urgentAnomalies && !prefs.extremeWeatherWarnings
                      ? ['Extreme Weather']
                      : []),
                    ...(prefs.roadClosures && !prefs.roadClosuresRestrictions
                      ? ['Restrictions']
                      : []),
                  ]}
                  onSelectHotspot={handleSelectHighRiskHotspot}
                  onSelectCap={handleSelectCapAlert}
                  onSelectSmoke={handleSelectSmokeAnomaly}
                  onSelectIce={handleSelectIceAnomaly}
                  onSelectFlood={handleSelectFloodAnomaly}
                  onSelectMediaSignal={handleSelectMediaSignalAnomaly}
                  onSelectOcFire={handleSelectFire}
                  onFlyTo={flyToIncident}
                  onCountChange={handleUrgentAlertCount}
                  reachable={apiHealth.reachable}
                />
              )
            }
            layers={
              <>
                <LayerToggle
                  embedded
                  prefs={prefs}
                  onChange={handlePrefsChange}
                  cameraCount={cameraCount}
                  sensorCount={sensorCount}
                  assetCounts={assetCounts}
                  aircraftCount={aircraftCount}
                  fireCount={fireCount}
                  /* #325 — same honest-degradation gate: the Layers "Urgent Alerts" count
                 blanks (UNKNOWN) while /api is unreachable rather than showing a stale
                 frozen number. Reachable path unchanged. */
                  urgentAlertCount={apiHealth.reachable ? urgentAlertCount : null}
                  areaCommandCount={areaCommandCount}
                  ontarioCorpsPartners={ontarioCorpsPartners}
                  feedHealth={feedHealth}
                  roadClosureCoverage={roadClosureCoverage}
                />
                {/* #621 Watch line — standing awareness of monitored & unfought OC fires near
                communities. NO map icon / layer / pref (the default view is unchanged). */}
                <OcFireWatchLine />
              </>
            }
            news={<NewsSocialFeed surface="rail" />}
            coordination={
              <CoordinationPanel
                onSelectIncident={handleSelectIncident}
                onSelectMission={handleSelectDroneMission}
                onSelectTask={handleSelectAgencyTask}
                onSelectInboxTask={handleSelectInboxTask}
                onFlyTo={flyToIncident}
                onActiveCountChange={setCoordinationCount}
              />
            }
            coordinationCount={coordinationCount}
          />
          {/* MAP REGION — docked command bar (40px) atop the map. All map-region
          chrome positions relative to this column, never the viewport
          (design council Part IV, region law). */}
          <div className="relative flex min-w-0 flex-1 flex-col">
            <CommandBar
              onFlyHome={flyHome}
              onFlyNational={flyNational}
              onSetHome={setHome}
              homeJustSet={homeJustSet}
              savedRegions={savedRegions}
              onFlyRegion={handleFlyRegion}
              onSaveRegion={handleSaveRegion}
              onDeleteRegion={handleDeleteRegion}
              currentBbox={currentBbox}
              selectedIncident={sitrepIncident}
              onOpenSitrep={setActiveSitrep}
              incidentSelected={!!sitrepIncident}
              drawingSector={drawingSector}
              placingCommunity={placingCommunity}
              onDrawSector={startSectorDraw}
              onDeclareCommunity={startDeclareCommunity}
              briefingMode={prefs.briefingMode}
              onSetBriefingMode={setBriefingMode}
              onOpenPanel={openOperationsPanel}
              onOpenShiftLog={() => setShiftLogOpen(true)}
              seatProfiles={seatProfiles}
              onApplySeatProfile={handleApplySeatProfile}
              onSaveSeatProfile={handleSaveSeatProfile}
              onDeleteSeatProfile={handleDeleteSeatProfile}
              search={
                <SearchBox
                  onDispatchPlace={handleDispatchPlace}
                  onDispatchEntity={handleDispatchEntity}
                />
              }
            />
            {/* #325 — honest-degradation banner: a full-width, static fault strip directly
            under the command bar that fires ONLY when /api is unreachable (≥3 consecutive
            failures). It asserts "cannot confirm", never a count — so a total outage can
            no longer read as a quiet all-clear board. */}
            <ApiHealthBanner
              reachable={apiHealth.reachable}
              lastReachedAt={apiHealth.lastReachedAt}
            />
            <div className="relative flex min-h-0 flex-1">
              {/* #180 — AAR replay scrub chrome (contextual-only: exists while the
          operator is replaying an incident; bottom of the MAP REGION). */}
              {aarReplayData && (
                <AarReplayOverlay
                  data={aarReplayData}
                  onSeek={(t) => aarReplayControllerRef.current?.setTime(t)}
                  onExit={exitAarReplay}
                />
              )}
              {/* Task #64 — sector naming form (after a polygon is drawn), anchored to the
          map region's top-left, clear of the command bar. */}
              {pendingSector && (
                <div
                  className="absolute left-3 top-3 z-[70] w-80 space-y-2 rounded-lg border border-slate-600 bg-slate-900/95 p-4 shadow-2xl backdrop-blur"
                  data-testid="sector-name-form"
                >
                  <div className="text-sm font-semibold text-amber-200">Name this sector</div>
                  <input
                    data-testid="sector-name-input"
                    value={pendingName}
                    autoFocus
                    onChange={(e) => setPendingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitPendingSector();
                      if (e.key === 'Escape') cancelPendingSector();
                    }}
                    placeholder='e.g. "North Sector A"'
                    className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[13px] text-slate-100"
                  />
                  <input
                    data-testid="sector-lead-input"
                    value={pendingLead}
                    onChange={(e) => setPendingLead(e.target.value)}
                    placeholder="Sector Lead (free text, optional)"
                    className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[13px] text-slate-100"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="sector-create-btn"
                      onClick={submitPendingSector}
                      className="rounded border border-amber-500 bg-amber-600/80 px-3 py-1 text-[12px] font-medium text-white hover:bg-amber-600"
                    >
                      Create Sector
                    </button>
                    <button
                      type="button"
                      onClick={cancelPendingSector}
                      className="rounded border border-slate-700 bg-slate-800 px-3 py-1 text-[12px] text-slate-300 hover:bg-slate-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {/* W.2 editor — declare form, opened once a point is placed (fixed modal). */}
              {pendingDeclare && (
                <CommunityEntityForm
                  mode="declare"
                  coords={pendingDeclare}
                  onClose={() => setPendingDeclare(null)}
                  onSubmitted={handleCommunityDeclared}
                />
              )}
              {/* #445-(3) — the record-evacuation-order modal, opened once the area is drawn. */}
              {pendingEvacOrder && (
                <EvacuationOrderForm
                  ring={pendingEvacOrder.ring}
                  preseed={pendingEvacOrder.preseed}
                  geometrySource={pendingEvacOrder.geometrySource}
                  onRedraw={pendingEvacOrder.geometrySource ? startEvacRedraw : undefined}
                  onClose={() => {
                    evacuationOrderControllerRef.current?.clearPreview();
                    setPendingEvacOrder(null);
                  }}
                  onSubmitted={(feature) => {
                    evacuationOrderControllerRef.current?.clearPreview();
                    setPendingEvacOrder(null);
                    // Surface what was just recorded: turn the (default-OFF) layer ON, refresh
                    // it, and open its detail panel.
                    setPrefs((prev) => ({ ...prev, operatorEvacuationOrders: true }));
                    evacuationOrderControllerRef.current?.refreshOrders();
                    setSelected({ kind: 'operatorEvacOrder', feature });
                  }}
                />
              )}
              <MapView
                showCameras={prefs.cameras}
                showSensors={prefs.sensors}
                smokePriorityOnly={prefs.smokePriorityOnly}
                showFireStations={prefs.fireStations}
                showWildfireBases={prefs.wildfireBases}
                showAviationBases={prefs.aviationBases}
                showOntarioCorps={prefs.ontarioCorps}
                hiddenPartners={hiddenPartnerSet}
                showLiveAircraft={prefs.liveAircraft}
                hiddenAircraftRoles={hiddenAircraftRoleSet}
                showFireRisk={anyWildfireLayerOn(prefs)}
                // The stage/response fetch filters flow from the prefs helpers — NOT inline
                // literals. The old inline `['FUL']` re-narrowed the default OC layer to
                // full-response only, silently undoing #607's relaxation (App never called the
                // helper that fixed it). fireResponseTypeFilter returns undefined for the default
                // OC layer so ALL OC postures (Full / Modified / Monitored / not-reported) reach
                // the client and the LOD governs visibility. (`?? undefined`: the helpers return
                // null only when neither wildfire layer is on, when showFireRisk already hides it.)
                fireStages={fireStageFilter(prefs) ?? undefined}
                fireResponseTypes={fireResponseTypeFilter(prefs) ?? undefined}
                // #180 — while the AAR replay is open the LIVE urgent-alert families
                // (all five gate on showHighRiskHotspots) + CAP warnings are hidden so
                // historical recolours can't be mistaken for live pulses; the overlay's
                // NOT-LIVE banner states this. Restored on exit.
                showHighRiskHotspots={prefs.urgentAnomalies && !aarReplayData}
                showRawHotspots={prefs.rawHotspots}
                showFusionCandidates={prefs.fusionCandidates}
                showCameraSmoke={prefs.cameraSmoke}
                showPowerOutages={prefs.powerOutages}
                showRoadClosures={prefs.roadClosures}
                roadClosuresRestrictions={prefs.roadClosuresRestrictions}
                onRoadClosureCountChange={setRoadClosureCount}
                showCommsDegradationZones={prefs.commsDegradationZones}
                showHydrometricGauges={prefs.hydrometricGauges}
                showFloodInundation={prefs.floodInundation}
                viewshedOnSelect={prefs.viewshedOnSelect}
                viewshedCamera={selected?.kind === 'camera' ? selected.feature : null}
                showExtremeWeatherWarnings={
                  prefs.urgentAnomalies && prefs.extremeWeatherWarnings && !aarReplayData
                }
                arcgisCategoryVisibility={arcgisCategoryVisibility}
                wmsOverlayVisibility={wmsOverlayVisibility}
                showFwiHeatmap={prefs.fwiHeatmap}
                showProvincialBoundaries={prefs.provincialBoundaries}
                showDroneCoverage={prefs.droneCoverage}
                showStagingAreas={prefs.stagingAreas}
                showAreaCommand={prefs.areaCommand}
                onSelectAreaCommand={handleSelectIncident}
                onAreaCommandCountChange={handleAreaCommandCountChange}
                showSectors={prefs.sectors}
                sectorIncidentId={sectorIncidentId}
                showCommunities={
                  prefs.floodVulnerableCommunities || prefs.iceStormVulnerableCommunities
                }
                communityStatuses={communityStatuses}
                communityVulnerabilityShading={prefs.communityVulnerabilityShading}
                selectedIncidentPoint={incidentPoint}
                onSelectCamera={handleSelectCamera}
                onSelectCameraCluster={handleSelectCameraCluster}
                onSelectIncidentCluster={handleSelectIncidentCluster}
                onSelectSensor={handleSelectSensor}
                onSelectAsset={handleSelectAsset}
                onSelectAircraft={handleSelectAircraft}
                onSelectFire={handleSelectFire}
                onSelectHighRiskHotspot={handleSelectHighRiskHotspot}
                onSelectRawHotspot={handleSelectRawHotspot}
                onSelectCameraSmoke={handleSelectCameraSmoke}
                onSelectUtilityOutage={handleSelectUtilityOutage}
                onSelectRoadClosure={handleSelectRoadClosure}
                onSelectArcgisFeature={handleSelectArcgisFeature}
                onSelectCommsDegradationZone={handleSelectCommsDegradationZone}
                onSelectCapAlert={handleSelectCapAlert}
                onSelectMarineVessel={handleSelectMarineVessel}
                onSelectDroneCoverage={handleSelectDroneCoverage}
                onSelectStagingArea={handleSelectStagingArea}
                onSelectSector={handleSelectSector}
                onSectorDrawn={handleSectorDrawn}
                onSectorCountChange={() => undefined}
                showOperatorEvacOrders={prefs.operatorEvacuationOrders}
                onEvacOrderDrawn={handleEvacOrderDrawn}
                onSelectOperatorEvacOrder={handleSelectOperatorEvacOrder}
                onSelectSmokeAnomaly={handleSelectSmokeAnomaly}
                onSelectIceAnomaly={handleSelectIceAnomaly}
                onSelectFloodAnomaly={handleSelectFloodAnomaly}
                onSelectHydrometricGauge={handleSelectHydrometricGauge}
                onSelectCommunity={handleSelectCommunity}
                onCommunityPlaced={handleCommunityPlaced}
                onCommunityPlaceStateChange={handleCommunityPlaceStateChange}
                onCommunitiesInView={handleCommunitiesInView}
                onCameraCountChange={handleCameraCount}
                onSensorCountChange={handleSensorCount}
                onAssetCountsChange={handleAssetCounts}
                onAircraftCountChange={handleAircraftCount}
                onFireCountChange={handleFireCount}
                onHighRiskCountChange={() => undefined}
                onVesselCountChange={() => undefined}
                onDroneCoverageCountChange={() => undefined}
                onStagingAreaCountChange={() => undefined}
                onSmokeAnomalyCountChange={() => undefined}
                onIceAnomalyCountChange={handleIceAnomalyCount}
                onFloodAnomalyCountChange={handleFloodAnomalyCount}
                onBboxChange={handleBboxChange}
                flyControllerRef={flyControllerRef}
                sectorControllerRef={sectorControllerRef}
                evacuationOrderControllerRef={evacuationOrderControllerRef}
                communityControllerRef={communityControllerRef}
                floodInundationControllerRef={floodInundationControllerRef}
                flythroughControllerRef={flythroughControllerRef}
                aarReplayControllerRef={aarReplayControllerRef}
                pulsingCameraIds={pulsingCameraIds}
              />
              {/* #177 — modelled flood-level control, shown only while the layer is on. */}
              {prefs.floodInundation && (
                <FloodInundationControl controllerRef={floodInundationControllerRef} />
              )}
              {/* Transient tool-status strip (council Part IV) — the per-tool hint pills
          merge into one strip that exists only while a map tool is active. */}
              {(drawingSector || drawingEvacOrder || placingCommunity) && (
                <div
                  data-testid="tool-status-strip"
                  className="pointer-events-none absolute bottom-3 left-1/2 z-[60] -translate-x-1/2 rounded-full border border-slate-600 bg-slate-900/90 px-3 py-1.5 text-[11px] text-slate-300 shadow-lg backdrop-blur"
                >
                  {drawingSector || drawingEvacOrder
                    ? 'Click to add vertices · double-click to finish · right-click / Esc to cancel'
                    : 'Click the map to place the community · right-click / Esc to cancel'}
                </div>
              )}
              {/* FWI heatmap colour legend (E.2.2.5) — bottom-left of the MAP REGION,
          only while the layer is active (was buried under the rail at left-56). */}
              {prefs.fwiHeatmap && <FwiLegend />}
            </div>
          </div>
          {/* Genuinely-wide enhancement (#131/#192): the SITREP document docks as an
          in-flow ~720px reading column to the LEFT of the detail panel. On
          standard displays it falls through to the centred modal rendered after
          the panel. */}
          {activeSitrep && dockSitrep && (
            <SitrepDetailPanel sitrep={activeSitrep} onClose={() => setActiveSitrep(null)} docked />
          )}
          {/* #381 — Shift Log drawer: relocated off the left rail to the Settings ▾
          menu; opens here as a left-edge slide-over over the map region. */}
          {shiftLogOpen && (
            <>
              <div
                data-testid="shift-log-backdrop"
                onClick={() => setShiftLogOpen(false)}
                className="absolute inset-0 z-[55] bg-black/40"
              />
              <div
                data-testid="shift-log-drawer"
                className="absolute left-0 top-0 z-[60] flex h-full w-[var(--rail-w)] max-w-[88vw] flex-col border-r border-slate-700 bg-slate-900 shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-slate-700 px-3 py-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-200">
                    Shift Log
                  </h2>
                  <button
                    type="button"
                    data-testid="shift-log-drawer-close"
                    onClick={() => setShiftLogOpen(false)}
                    aria-label="Close shift log"
                    className="px-1 text-lg leading-none text-slate-400 hover:text-slate-100"
                  >
                    ×
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-hidden">
                  <ShiftLogPanel currentBbox={currentBbox} onOpenSitrep={setActiveSitrep} />
                </div>
              </div>
            </>
          )}
          <SidePanel
            selected={selected}
            loadingCameras={loadingCameras}
            loadingSensors={loadingSensors}
            isCameraPinned={isCameraPinned}
            onTogglePinCamera={handleTogglePinCamera}
            onViewshedOnSelectChange={setViewshedOnSelect}
            viewshedOnSelect={prefs.viewshedOnSelect}
            onSelectCamera={handleSelectCamera}
            onSelectIncident={handleSelectColocatedIncident}
            onSelectAsset={handleSelectAsset}
            onSelectAircraft={handleSelectAircraft}
            incidentPoint={incidentPoint}
            incidentContextId={workingIncidentId}
            // #428 — the resolved incidents.id for the current selection, packed into a
            // wall escalation so the wall renders THAT incident's sectors.
            escalationIncidentId={selectedIncidentId}
            onSelectMission={handleSelectDroneMission}
            onSmokeResolved={handleSmokeResolved}
            onIceResolved={handleIceResolved}
            onFloodResolved={handleFloodResolved}
            onMediaSignalResolved={handleMediaSignalResolved}
            onCommunityChanged={handleCommunityChanged}
            onMoveCommunity={startMoveCommunity}
            onRecordEvacOrder={startCommunityEvacuation}
            onOperatorEvacOrderChanged={handleOperatorEvacOrderChanged}
            onCorrectEvacOrderGeometry={handleCorrectEvacGeometry}
            onEnsureOperatorEvacLayerVisible={handleEnsureEvacLayerVisible}
            onCommunityEntityChanged={handleCommunityEntityChanged}
            onCommunityRetired={handleCommunityRetired}
            onOpenFusedHotspot={handleOpenFusedHotspot}
            onFlyToIncident={flyToIncident}
            onOpenEvacuation={handleOpenEvacuation}
            onFlyToCommunity={flyToIncident}
            onViewAsCommunity={handleViewAsCommunity}
            onStagingAreaChanged={handleStagingAreaChanged}
            onSectorChanged={handleSectorChanged}
            onEditSectorGeometry={handleEditSectorGeometry}
            onDrawSector={startSectorDraw}
            onOpenSitrep={handleOpenSitrep}
            onOpenConnectedAssist={openConnectedAssist}
            onFlythrough={toggleFlythrough}
            flythroughActive={flythroughActive}
            onOpenReplay={(incidentId) => void openAarReplay(incidentId)}
            aarReplayActive={!!aarReplayData}
          />
          {connectedAssist && (
            <AssistanceRequestDetail
              request={connectedAssist}
              onClose={() => setConnectedAssist(null)}
              onUpdated={setConnectedAssist}
            />
          )}
          {activeSitrep && !dockSitrep && (
            <SitrepDetailPanel sitrep={activeSitrep} onClose={() => setActiveSitrep(null)} />
          )}
          {overlayCamera && (
            <CameraOverlay
              camera={overlayCamera}
              onClose={closeOverlay}
              pinned={isCameraPinned(overlayCamera.id)}
              onTogglePin={() => handleTogglePinCamera(overlayCamera)}
            />
          )}
        </>
      )}
      {/* #458 (council §3) — the PEOC "view as community" lens banner. A loud,
          dismissible LENS indicator, NOT a mode: it never blocks the map (you are
          glancing AT the map) and Esc / Exit restores the operator's own view. */}
      {viewAsCommunity && (
        <div
          data-testid="view-as-community-banner"
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[1200] flex items-center gap-3 rounded-full border border-indigo-400/60 bg-indigo-950/90 px-4 py-2 shadow-lg backdrop-blur"
        >
          <span aria-hidden className="text-indigo-300">
            👁
          </span>
          <span className="text-sm text-indigo-100">
            Viewing as <span className="font-semibold">{viewAsCommunity.properties.name}</span> —
            municipal COP
          </span>
          <button
            type="button"
            data-testid="view-as-community-exit"
            onClick={exitViewAsCommunity}
            className="ml-1 rounded-full border border-indigo-400/60 bg-indigo-800/70 px-2.5 py-0.5 text-xs font-medium text-indigo-100 hover:bg-indigo-700"
          >
            Exit lens
          </button>
        </div>
      )}
      <ToastHost />
    </div>
  );
}
